import { getTrack, getSelectedTrack } from '../api.js';
import { setNavigationGuard, clearNavigationGuard } from '../router.js';
import { getTeam } from '../api-teams.js';
import { getCar } from '../api-cars.js';
import { getDriver } from '../api-drivers.js';
import { getSeries } from '../api-series.js';
import { mountSimulator } from '../../../simulator/src/main.js';
import { fmtLap, fmtSector } from '../../../simulator/src/lap-timing.js';
import { getFinalClassification, DEFAULT_RACE_LAPS } from '../../../simulator/src/race-state.js';
import { fmtSpeed } from '../../../shared/units.js';
import { getCompound } from '../../../shared/tire-compounds.js';
import { loadTrack } from '../../../shared/track-schema.js';
import { nearestPointOnDense } from '../../../shared/geometry.js';
import { arcLengthAtNp } from '../../../shared/racing-line.js';
import { driverPersonalityFromAttributes } from '../../../shared/driver-personality.js';
import { getEditorSessionTrack } from './editor.js';
import { getRaceSetup } from './race-setup.js';


// Display-only damage scaling. Raw `cond.damage` is 0..1; a car
// retires at 0.5 (see race-state.js + condition.js — the totaled
// threshold is load-bearing for retirement and pit-AI logic and stays
// where it is). For UI we want the percentage to read 0% (pristine)
// to 100% (totaled) so the player isn't confused by a "50% damage =
// dead" reading. Anything past the threshold (cars taking more hits
// while wrecked) clamps at 100% so the bar / readout don't show 120%.
const DAMAGE_TOTALED_THRESHOLD = 0.5;
const damageDisplayPct = (raw) =>
  Math.max(0, Math.min(100, Math.round((raw / DAMAGE_TOTALED_THRESHOLD) * 100)));


// Resolve a roster setup (track + seriesId) into the per-car entries
// array the simulator consumes. The series picks the team list (one
// or many classes); each team contributes one entry per driver
// assigned to it. Each entry carries its `classId` / `className` so
// the simulator can later surface multi-class results without a
// second resolve. Failed lookups are dropped silently — the picker
// pre-validates against the live catalog so the only way to land
// here with a missing ref is editing files between picker and start,
// which is rare and self-explanatory once the grid shrinks.
//
// Backwards compat: if the bridge payload still has the legacy
// `teamIds` shape (older sessionStorage that survived a refresh),
// fall back to it so the user isn't surprised by an empty grid.
async function buildEntriesFromSetup(setup) {
  if (!setup) return null;

  // Build the (teamId, classId, className) tuple list. Series-driven
  // path is the primary one; teamIds[] is a transitional fallback.
  let teamPlan = [];
  if (typeof setup.seriesId === 'string') {
    const series = await getSeries(setup.seriesId).catch(() => null);
    if (!series) return null;
    for (const cls of series.classes) {
      for (const tid of cls.teamIds) {
        teamPlan.push({ teamId: tid, classId: cls.id, className: cls.name });
      }
    }
  } else if (Array.isArray(setup.teamIds) && setup.teamIds.length > 0) {
    teamPlan = setup.teamIds.map(tid => ({ teamId: tid, classId: null, className: '' }));
  } else {
    return null;
  }

  // Resolve teams in parallel. Use a Map keyed by teamId so we don't
  // re-fetch a team that appears twice (shouldn't happen — schema
  // dedupes within a series — but cheap to be defensive).
  const teamsById = new Map();
  await Promise.all(teamPlan.map(async (p) => {
    if (teamsById.has(p.teamId)) return;
    const t = await getTeam(p.teamId).catch(() => null);
    if (t) teamsById.set(p.teamId, t);
  }));
  const plan = teamPlan.filter(p => teamsById.has(p.teamId));
  if (plan.length === 0) return null;

  // Lazy car + driver fetches — same chassis or driver may be
  // referenced by multiple teams. Cache by id within this build.
  const carCache = new Map();
  const driverCache = new Map();
  const fetchCar = async (id) => {
    if (!id) return null;
    if (!carCache.has(id)) carCache.set(id, await getCar(id).catch(() => null));
    return carCache.get(id);
  };
  const fetchDriver = async (id) => {
    if (!id) return null;
    if (!driverCache.has(id)) driverCache.set(id, await getDriver(id).catch(() => null));
    return driverCache.get(id);
  };

  const entries = [];
  for (const p of plan) {
    const team = teamsById.get(p.teamId);
    const car = await fetchCar(team.carId);
    if (!car) continue; // can't race without a chassis
    const livery = {
      body:   team.livery?.primary || '#cccccc',
      accent: team.livery?.accent  || '#888888',
    };
    for (const slot of team.entries) {
      const driver = await fetchDriver(slot.driverId);
      if (!driver) continue;
      entries.push({
        spec: car.spec,
        livery,
        driverProfile: {
          ...driverPersonalityFromAttributes(driver.attributes),
          // Stash the raw attribute set on the profile so future
          // controllers can read fields directly (wetWeather,
          // tyreManagement, ...) without a round-trip through the
          // 2-dim collapse. Keeps backwards-compat with the existing
          // controller, which only reads aggression + skill.
          attributes: { ...driver.attributes },
        },
        // Driver id from the team's slot reference, NOT driver.id —
        // getDriver returns the schema-loaded object which doesn't
        // carry id (only listDrivers' summarize step attaches it).
        // slot.driverId is what we used to fetch the driver, so it's
        // the canonical id that downstream lookups (solo-mode resolve)
        // will compare against.
        driverId: slot.driverId,
        displayName: driver.shortName || driver.name,
        fullName: driver.name,
        carNumber: typeof slot.carNumber === 'number' ? slot.carNumber : driver.preferredNumber ?? null,
        team: { id: team.shortName ? team.shortName.toLowerCase() : team.name, name: team.name, shortName: team.shortName },
        // Class identity from the series. Single-class series get a
        // class with no name — kept anyway so per-class consumers
        // (future multi-class T&S, results) don't branch on missing
        // fields. classId is null only on the legacy teamIds-only
        // fallback path.
        classId:   p.classId,
        className: p.className,
      });
    }
  }
  if (entries.length === 0) return null;
  // Random grid order. Until qualifying exists, the order teams were
  // picked in shouldn't dictate where cars line up — that would make
  // every race a procession from "first selected" to "last selected".
  // Fisher-Yates shuffle, in place. Resetting cars to grid via the
  // R key keeps the same shuffled positions; revisiting the race URL
  // re-runs this function and gets a fresh order.
  for (let i = entries.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [entries[i], entries[j]] = [entries[j], entries[i]];
  }
  return entries;
}

// Right-side car-spec panel — flip to true to bring the live tuning
// sliders back. Hidden by default while the manager-game UX is
// stabilizing (per-team spec authoring lives in the roster editor
// now; in-race tuning would compete with that flow). Wiring + HTML
// are gated together so the panel costs zero DOM / event listeners
// when off.
const SHOW_SPEC_PANEL = false;

// ----------------------------------------------------------------
// LIVE TUNING — per-car spec sliders. Ported from the prototype's
// race-prototype.html which had a collapsible right-side tuning
// panel. Storage is SI throughout (matches DEFAULT_CAR_SPEC); each
// `tunable` entry's units block converts on display + on input.
//
// When a different car is selected, the panel rebuilds against that
// car's spec — every car owns its own values (createCar shallow-
// clones spec on construction), so a slider drag affects only the
// selected car.
// ----------------------------------------------------------------
const TUNABLE = [
  { key: 'enginePower', label: 'Engine Power', units: {
      metric:   { unit: 'kW', min: 50,  max: 800,  step: 10, toSI: v => v * 1000,    fromSI: v => v / 1000,    fmt: v => Math.round(v) },
      imperial: { unit: 'hp', min: 65,  max: 1075, step: 10, toSI: v => v * 745.7,   fromSI: v => v / 745.7,   fmt: v => Math.round(v) },
  }},
  { key: 'brakeForce', label: 'Brake Force', units: {
      metric: { unit: 'kN', min: 5, max: 40, step: 0.5, toSI: v => v * 1000, fromSI: v => v / 1000, fmt: v => v.toFixed(1) },
  }},
  { key: 'mass', label: 'Mass', units: {
      metric:   { unit: 'kg', min: 400, max: 1500, step: 25, toSI: v => v,           fromSI: v => v,           fmt: v => Math.round(v) },
      imperial: { unit: 'lb', min: 880, max: 3300, step: 50, toSI: v => v / 2.2046,  fromSI: v => v * 2.2046,  fmt: v => Math.round(v) },
  }},
  { key: 'grip', label: 'Lateral Grip', units: {
      metric: { unit: 'g', min: 0.6, max: 2.5, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'tractionGrip', label: 'Traction Grip', units: {
      metric: { unit: 'g', min: 0.6, max: 3.0, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'maxSteeringAngle', label: 'Max Steering', units: {
      metric: { unit: '°', min: 10, max: 45, step: 1, toSI: v => v * Math.PI / 180, fromSI: v => v * 180 / Math.PI, fmt: v => Math.round(v) },
  }},
  { key: 'steeringRate', label: 'Steer Rate', units: {
      metric: { unit: 'rad/s', min: 1, max: 10, step: 0.2, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(1) },
  }},
  { key: 'steerSpeedRef', label: 'Steer Speed Ref', units: {
      metric:   { unit: 'km/h', min: 20, max: 200, step: 5, toSI: v => v / 3.6,    fromSI: v => v * 3.6,    fmt: v => Math.round(v) },
      imperial: { unit: 'mph',  min: 12, max: 125, step: 3, toSI: v => v / 2.2369, fromSI: v => v * 2.2369, fmt: v => Math.round(v) },
  }},
  { key: 'minSteerMul', label: 'Min Steer Auth', units: {
      metric: { unit: '×', min: 0.05, max: 1.0, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'dragCoeff', label: 'Drag Coeff', units: {
      metric: { unit: '', min: 0.1, max: 2.5, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'wheelbase', label: 'Wheelbase', units: {
      metric:   { unit: 'm',  min: 1.8, max: 4.0,  step: 0.1, toSI: v => v,          fromSI: v => v,          fmt: v => v.toFixed(1) },
      imperial: { unit: 'ft', min: 5.9, max: 13.1, step: 0.3, toSI: v => v / 3.2808, fromSI: v => v * 3.2808, fmt: v => v.toFixed(1) },
  }},
  { key: 'yawInertia', label: 'Yaw Inertia', units: {
      metric: { unit: 'kg·m²', min: 400, max: 3000, step: 50, toSI: v => v, fromSI: v => v, fmt: v => Math.round(v) },
  }},
  { key: 'frontGripBias', label: 'Front Grip', units: {
      metric: { unit: '', min: 0.35, max: 0.65, step: 0.01, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'corneringStiffness', label: 'Tire Stiffness', units: {
      metric: { unit: '', min: 3, max: 25, step: 0.5, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(1) },
  }},
  { key: 'yawDamping', label: 'Yaw Damping', units: {
      metric: { unit: '', min: 0.0, max: 3.0, step: 0.1, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(1) },
  }},
];

// Driver-profile sliders. Same shape as TUNABLE but operates on
// car.driverProfile, not car.spec. Forward-compat for the personality
// work — adding a new profile knob means adding an entry here.
const DRIVER_TUNABLE = [
  { key: 'aggression', label: 'Aggression', units: {
      metric: { unit: '', min: 0.0, max: 1.0, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
  { key: 'skill', label: 'Skill', units: {
      metric: { unit: '', min: 0.0, max: 1.0, step: 0.05, toSI: v => v, fromSI: v => v, fmt: v => v.toFixed(2) },
  }},
];

// Same key the editor uses, so unit preference travels with the user
// across pages. Values are 'metric' | 'imperial'; default imperial.
const UNIT_STORAGE_KEY = 'trackEditorUnits';
function readStoredUnit() {
  if (typeof localStorage === 'undefined') return 'imperial';
  const v = localStorage.getItem(UNIT_STORAGE_KEY);
  return v === 'metric' || v === 'imperial' ? v : 'imperial';
}
function writeStoredUnit(u) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(UNIT_STORAGE_KEY, u);
}

function unitCfg(param, unitSystem) {
  return param.units[unitSystem] || param.units.metric;
}

// Pre-race overlay. Mode + driver + lap count are now picked on the
// race-setup page (race-setup.js); the in-canvas overlay collapses
// to a single "Start race session" placeholder that calls
// sim.startRace() with the bundle the setup page wrote into
// sessionStorage. The placeholder phrasing leaves room for future
// session types (practice / qualifying / heats / race) without a
// redesign — same button, different label.
//
// `setup` is the resolved bundle: { raceMode, soloCarIdx?, totalLaps }.
// `soloCarIdx` is mapped from the setup's `soloDriverId` by the race
// page's mount flow, since car indices don't exist until the
// simulator builds the world.
//
// One-shot: removes itself once Start is clicked.
function runPreRaceOverlay(mountEl, sim, setup) {
  if (mountEl.querySelector('.race-prerace')) return;
  const { raceMode = 'god', soloCarIdx = null, totalLaps = DEFAULT_RACE_LAPS } = setup || {};

  // Meta-line parts so the player can sanity-check the setup before
  // committing. Order is broadcast-vocabulary: TRACK · LAPS · MODE
  // (and driver when solo). Driver name lookup runs against the live
  // world roster rather than re-reading the bundle.
  const trackName = sim.world.track?.name || 'Unknown circuit';
  const lapsStr   = `${totalLaps} lap${totalLaps === 1 ? '' : 's'}`;
  const modeStr   = raceMode === 'solo' ? 'Solo' : 'God mode';
  let driverStr   = '';
  if (raceMode === 'solo' && Number.isFinite(soloCarIdx)) {
    const car = sim.world.cars[soloCarIdx];
    if (car) {
      const num  = typeof car.carNumber === 'number' ? `#${car.carNumber} ` : '';
      const name = car.fullName || car.displayName || car.id?.toUpperCase() || `CAR${soloCarIdx}`;
      driverStr  = `${num}${name}`;
    }
  }
  const metaParts = [trackName, lapsStr, modeStr, driverStr].filter(Boolean);

  const overlay = document.createElement('div');
  overlay.className = 'race-prerace';
  overlay.innerHTML = `
    <div class="race-prerace-card">
      <div class="race-prerace-header">
        <div class="race-prerace-tag">// Pre-race</div>
        <h2 class="race-prerace-title">Gentlemen, <span class="accent">start your engines.</span></h2>
        <div class="race-prerace-meta">${metaParts.map(escapeHtml).join(' <span class="race-prerace-meta-sep">·</span> ')}</div>
      </div>
      <div class="race-prerace-actions">
        <button type="button" class="race-prerace-start">Start race session <span class="race-prerace-arrow">→</span></button>
      </div>
    </div>
  `;
  mountEl.appendChild(overlay);

  overlay.querySelector('.race-prerace-start').addEventListener('click', () => {
    overlay.remove();
    // Starting-lights gantry. Replaces the old 3-2-1 number countdown
    // with an F1-style 5-light sequence: lights come on one at a time,
    // hold briefly, then all extinguish — the race starts on
    // lights-out, NOT when the lights have all come on. The random
    // hold mimics the real-world "you can't time the start", though
    // here it's pure UX flavor (physics is paused until startRace).
    runStartingLights(mountEl, () => {
      const ok = sim.startRace({ raceMode, soloCarIdx, totalLaps });
      if (!ok) return;
      if (raceMode === 'solo' && Number.isFinite(soloCarIdx)) {
        sim.selectCar(soloCarIdx);
      }
    });
  });
}

// F1-style starting lights. Five red lights, each turning on at a
// fixed cadence (~1 s apart). After the 5th, a short random hold
// (0.8–2.3 s) builds tension. Then all lights extinguish at once —
// that's the green flag. `onLightsOut` fires at that moment so the
// host can call sim.startRace(). The overlay fades out shortly after
// to keep the lights briefly visible against the live track.
function runStartingLights(mountEl, onLightsOut) {
  // Mount inside .race-stage so position:absolute is resolved against
  // the same positioning context the Race Control panel uses. mountEl
  // itself isn't position:relative, so appending there put the overlay
  // in viewport coordinates and our offset math landed in the wrong
  // frame, painting it under the topbar instead of below the panel.
  const stage = mountEl.querySelector('.race-stage') || mountEl;
  if (stage.querySelector('.race-lights')) return;
  const overlay = document.createElement('div');
  overlay.className = 'race-lights';
  overlay.innerHTML = `
    <div class="race-lights-row" role="status" aria-label="Starting lights">
      <div class="race-light"></div>
      <div class="race-light"></div>
      <div class="race-light"></div>
      <div class="race-light"></div>
      <div class="race-light"></div>
    </div>
  `;
  stage.appendChild(overlay);
  // Pin the gantry 12 px below the live Race Control panel. Now that
  // overlay and panel share .race-stage as their positioning context,
  // panelRect.bottom - stageRect.top is the right offset.
  const panel = stage.querySelector('#race-control-panel');
  if (panel) {
    const panelRect = panel.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const top = (panelRect.bottom - stageRect.top) + 12;
    overlay.style.top = `${top}px`;
  }
  const lights = overlay.querySelectorAll('.race-light');

  // Tuned for ~3 s total — same ballpark as the old 3-2-1 countdown.
  // Lights snap on at 500 ms intervals, hold briefly (300–800 ms),
  // then all out → race start. Overlay fades immediately so the
  // gantry doesn't linger over moving cars.
  const LIGHT_INTERVAL_MS = 500;
  const HOLD_MIN_MS       = 300;
  const HOLD_RAND_MS      = 500;
  const FADE_OUT_MS       = 250;

  let i = 0;
  const advance = () => {
    if (i >= 5) {
      const hold = HOLD_MIN_MS + Math.random() * HOLD_RAND_MS;
      setTimeout(() => {
        lights.forEach(l => l.classList.remove('on'));
        onLightsOut();
        overlay.style.transition = `opacity ${FADE_OUT_MS}ms ease-out`;
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), FADE_OUT_MS + 50);
      }, hold);
      return;
    }
    lights[i].classList.add('on');
    i++;
    setTimeout(advance, LIGHT_INTERVAL_MS);
  };
  // Brief lead-in so the first light isn't lit the instant the
  // gantry appears — feels less rushed at the start of the sequence.
  setTimeout(advance, 200);
}

// Flag-change gantry. Reuses the starting-lights layout but lights
// all five at once in flag color, holds for a few seconds, then
// fades. Triggered on caution start (yellow) and caution end (green)
// to give the player a clear visual cue alongside the toast +
// race-control flag pill. Independent from `runStartingLights` so a
// flag-change overlay can't collide with the pre-race countdown
// (different DOM node, different lifecycle).
function showFlagGantry(mountEl, color) {
  const stage = mountEl.querySelector('.race-stage') || mountEl;
  // Replace any in-flight flag gantry — back-to-back flag changes
  // (rare but possible: caution end and immediately a new incident)
  // should show the latest, not stack.
  const existing = stage.querySelector('.race-lights.flag-gantry');
  if (existing) existing.remove();
  const overlay = document.createElement('div');
  overlay.className = `race-lights flag-gantry flag-${color}`;
  overlay.innerHTML = `
    <div class="race-lights-row" role="status" aria-label="Flag change ${color}">
      <div class="race-light on"></div>
      <div class="race-light on"></div>
      <div class="race-light on"></div>
      <div class="race-light on"></div>
      <div class="race-light on"></div>
    </div>
  `;
  stage.appendChild(overlay);
  const panel = stage.querySelector('#race-control-panel');
  if (panel) {
    const panelRect = panel.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const top = (panelRect.bottom - stageRect.top) + 12;
    overlay.style.top = `${top}px`;
  }
  const HOLD_MS    = 2500;
  const FADE_OUT_MS = 400;
  setTimeout(() => {
    overlay.style.transition = `opacity ${FADE_OUT_MS}ms ease-out`;
    overlay.style.opacity = '0';
    setTimeout(() => overlay.remove(), FADE_OUT_MS + 50);
  }, HOLD_MS);
}

// Race results overlay. Renders once when the race state lands on
// FINISHED. Active finishers come first in their crossing order;
// retirees follow, sorted by laps completed + arc at retirement
// (most progress first) — getFinalClassification handles the merge.
// Idempotent — repeated calls are no-ops once the overlay is already
// mounted, so it's safe to invoke from the hud tick.
function showRaceResults(mountEl, sim, onRestart) {
  if (mountEl.querySelector('.race-results')) return;
  const rs = sim.world.raceState;
  if (!rs) return;

  const overlay = document.createElement('div');
  overlay.className = 'race-results';

  const classification = getFinalClassification(rs, sim.world.cars);
  const retiredSet = rs.retiredCars instanceof Map ? rs.retiredCars : null;

  // Fastest-lap holder across the field. Tie goes to the lower car
  // index — same precedence as F1 timing-tower display, and stable so
  // re-rendering the table doesn't flicker the highlight.
  let fastestCarIdx = -1;
  let fastestLapMs  = Infinity;
  for (let i = 0; i < sim.world.cars.length; i++) {
    const t = sim.world.cars[i]?.lapState?.bestLapTime;
    if (t != null && t < fastestLapMs) {
      fastestLapMs  = t;
      fastestCarIdx = i;
    }
  }

  // Highlighted row. God mode → the winner gets the accent stripe
  // (P1 is the story). Solo mode → the player's own driver gets it
  // regardless of where they finished, since the results are about
  // *their* race even when they came in 8th. Classification[0] is
  // already in finish order so winner === classification[0].
  const isSolo            = rs.raceMode === 'solo';
  const highlightCarIdx   = isSolo && Number.isFinite(rs.soloCarIdx)
    ? rs.soloCarIdx
    : classification[0];

  // Winner finish time anchors all gap calculations. The leader's row
  // shows total race time (finishedAt − startedAt); everyone else
  // shows gap to leader (their finishedAt − leader's). Retirees and
  // anyone with no finish stamp fall back to lap-down notation.
  const winnerCar     = sim.world.cars[classification[0]];
  const winnerFinish  = winnerCar?._finishedAt ?? rs.finishedAt ?? null;
  const raceStartedAt = rs.startedAt ?? null;
  const winnerLap     = winnerCar?._finishLapAt != null
    ? winnerCar._finishLapAt + 1
    : (winnerCar?.lapState?.lapNumber ?? 0);

  const fmtTotal = (secs) => {
    if (!Number.isFinite(secs) || secs < 0) return '—';
    const total = Math.floor(secs);
    const ms    = Math.round((secs - total) * 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total - h * 3600) / 60);
    const s = total - h * 3600 - m * 60;
    const ss  = String(s).padStart(2, '0');
    const mss = String(ms).padStart(3, '0');
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}.${mss}`;
    return `${m}:${ss}.${mss}`;
  };
  const fmtGapSecs = (secs) => {
    if (!Number.isFinite(secs) || secs < 0) return '—';
    if (secs < 60) return `+${secs.toFixed(3)}s`;
    const m = Math.floor(secs / 60);
    const s = secs - m * 60;
    return `+${m}:${s.toFixed(3).padStart(6, '0')}`;
  };

  const rows = classification.map((carIdx, pos) => {
    const car  = sim.world.cars[carIdx];
    if (!car) return '';
    const ls   = car.lapState;
    const num  = (typeof car.carNumber === 'number') ? `#${car.carNumber}` : '';
    // Results show prose form: full driver name + team abbreviation.
    // Falls back through displayName/id when a default-grid race has
    // no roster data attached.
    const driverName = car.fullName || car.displayName || car.id?.toUpperCase() || `CAR${carIdx}`;
    const team       = car.team?.shortName || '';
    const cond = car.condition;
    const isRetired = retiredSet?.has(carIdx) ?? false;
    // Reason source: condition flags at retirement time. Note we
    // read condition NOW, which is the same as at retirement since
    // condition is sticky (totaled stays totaled, blown stays blown,
    // empty fuel can't refill without a pit).
    const reason = isRetired
      ? (cond?.damage > 0.5 ? 'TOT' : cond?.blownTires ? 'TIRE' : cond?.fuel <= 0 ? 'FUEL' : 'DNF')
      : '';
    const best = ls?.bestLapTime != null
      ? `${(ls.bestLapTime / 1000).toFixed(3)}s`
      : '—';
    const isFastest = carIdx === fastestCarIdx;
    // "Laps completed" — sources differ by state:
    // • Retirees: snapshot taken at retirement (live lap drifts while
    //   the wreck limps to runoff).
    // • Clean finishers: _finishLapAt is the lap they were ON when
    //   the leader took the flag, which equals their completed-lap
    //   count. Reading lapState.lapNumber here inflates by 1 because
    //   it represents the *currently running* lap and the leader's
    //   checkered crossing already started the cool-down lap (so a
    //   20-lap race shows 21 for everyone on the lead lap).
    // • Fallback (shouldn't normally hit): live lapNumber.
    const lapsAtRetire = isRetired ? rs.retiredCars.get(carIdx)?.lap : null;
    const laps = lapsAtRetire ?? car._finishLapAt ?? ls?.lapNumber ?? 0;
    const stops = car.pitStops ?? 0;
    // Gap-to-leader text. Winner shows their total race time; same-lap
    // finishers show "+X.xxxs" relative to the winner; lap-down cars
    // show "+N lap"; retirees / unfinished show DNF (their reason
    // column already says why) or a dash if no data.
    let gapText = '—';
    if (pos === 0) {
      gapText = winnerFinish != null && raceStartedAt != null
        ? fmtTotal(winnerFinish - raceStartedAt)
        : '—';
    } else if (isRetired) {
      gapText = 'DNF';
    } else if (car._finishedAt != null && winnerFinish != null) {
      // Same-lap if their finish-lap matches the winner's; otherwise
      // report the lap deficit. _finishLapAt is the lap they were on
      // when the leader took the flag, so finishLap = _finishLapAt + 1.
      const myFinishLap = car._finishLapAt != null
        ? car._finishLapAt + 1
        : (ls?.lapNumber ?? 0);
      const lapsDown = winnerLap - myFinishLap;
      if (lapsDown > 0) {
        gapText = `+${lapsDown} lap${lapsDown === 1 ? '' : 's'}`;
      } else {
        gapText = fmtGapSecs(car._finishedAt - winnerFinish);
      }
    }
    // Net positions gained vs. starting grid. Positive = moved up the
    // order (started 8th, finished 3rd → +5). Missing startingPosition
    // (legacy cars from before this was tracked) renders as a dash.
    const startPos = car.startingPosition;
    const delta    = (typeof startPos === 'number') ? startPos - (pos + 1) : null;
    const deltaClass = delta == null ? 'zero'
                      : delta > 0    ? 'gain'
                      : delta < 0    ? 'loss'
                      : 'zero';
    const deltaText  = delta == null ? '—'
                      : delta > 0    ? `+${delta}`
                      : delta < 0    ? `${delta}`
                      : '—';
    const isHighlight = carIdx === highlightCarIdx;
    return `
      <tr class="race-results-row${isRetired ? ' retired' : ''}${isHighlight ? ' is-highlight' : ''}">
        <td class="race-results-pos">${pos + 1}</td>
        <td class="race-results-delta ${deltaClass}">${deltaText}</td>
        <td class="race-results-num">${num}</td>
        <td class="race-results-name">${driverName}<span class="race-results-team">${team ? ' ' + team : ''}</span></td>
        <td class="race-results-gap${pos === 0 ? ' leader' : ''}">${gapText}</td>
        <td class="race-results-laps">${laps}</td>
        <td class="race-results-stops">${stops}</td>
        <td class="race-results-best${isFastest ? ' is-fastest' : ''}" title="${isFastest ? 'Fastest lap of the race' : ''}">${isFastest ? '<span class="race-results-fl">FL</span> ' : ''}${best}</td>
        <td class="race-results-status">${reason}</td>
      </tr>
    `;
  }).join('');

  // Header context line — track + total laps + winning time so the
  // modal reads as a session summary, not just a standings table.
  const trackName    = sim.world.track?.name || 'Unknown circuit';
  const totalLapsStr = rs.totalLaps > 0 ? `${rs.totalLaps} lap${rs.totalLaps === 1 ? '' : 's'}` : '';
  const winnerTime   = winnerFinish != null && raceStartedAt != null
    ? fmtTotal(winnerFinish - raceStartedAt) : null;
  const winnerName   = winnerCar
    ? (winnerCar.fullName || winnerCar.displayName || winnerCar.id?.toUpperCase() || 'WINNER')
    : null;
  const metaParts = [trackName, totalLapsStr, winnerTime].filter(Boolean);
  overlay.innerHTML = `
    <div class="race-results-card">
      <div class="race-results-header">
        <div class="race-results-label">// Session results</div>
        <h2 class="race-results-title">${winnerName ? `${escapeHtml(winnerName)} <span class="race-results-title-tag">wins</span>` : 'Race results'}</h2>
        <div class="race-results-meta">${metaParts.map(escapeHtml).join(' <span class="race-results-meta-sep">·</span> ')}</div>
      </div>
      <table class="race-results-table">
        <thead>
          <tr><th>Pos</th><th title="Positions gained or lost from starting grid">+/-</th><th>#</th><th>Driver</th><th title="Total race time for the winner; gap to leader for everyone else">Gap</th><th>Laps</th><th>Stops</th><th>Best</th><th>DNF</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="race-results-actions">
        <button type="button" class="race-results-btn primary" id="race-results-restart">Restart race <span class="race-results-arrow">→</span></button>
        <a class="race-results-btn" href="#/tracks">Exit</a>
      </div>
    </div>
  `;
  mountEl.appendChild(overlay);
  // Restart button hooks back into the same restart pipeline the Esc
  // menu uses (sim.restartRace + drop pause + re-mount the prerace
  // overlay). The callback is owned by render() so it can close over
  // refreshPauseBtn / runPreRaceOverlay / DEFAULT_RACE_LAPS without
  // pushing those into module scope.
  if (typeof onRestart === 'function') {
    overlay.querySelector('#race-results-restart')?.addEventListener('click', onRestart);
  }
}

export async function render(mountEl, params) {
  // Track ID lives in sessionStorage instead of the URL. The page
  // distinguishes "no selection" (redirect to tracks list) from
  // "session-staged track" (`?source=session`, set by the editor's
  // Test Track flow) up below.
  const trackId = getSelectedTrack();
  const fromSession = params.source === 'session';
  if (!trackId && !fromSession) {
    mountEl.innerHTML = '<section class="page"><h1>No track selected</h1><p><a href="#/tracks">Pick a track</a></p></section>';
    return;
  }

  // Back-to-edit only makes sense when the user actually came from the
  // editor's "Test track" flow (which uses ?source=session). Direct
  // races from the tracks list have no editor context to return to.
  const backLinkHtml = fromSession
    ? `<a class="race-back" id="race-back" href="#/edit?source=session" title="Return to the editor">← Back to edit</a>`
    : '';

  mountEl.innerHTML = `
    <section class="race-stage">
      <canvas id="race-canvas"></canvas>

      <div class="race-debug-panel" id="race-debug-panel" hidden>
        <div class="rdb-title">DEBUG</div>
        <div class="rdb-row"><span class="rdb-label">FPS</span><span class="rdb-val" id="rdb-fps">—</span></div>
        <div class="rdb-row"><span class="rdb-label">Frame</span><span class="rdb-val" id="rdb-frame">—</span></div>
        <div class="rdb-row"><span class="rdb-label">Tick</span><span class="rdb-val" id="rdb-tick">—</span></div>
        <div class="rdb-row"><span class="rdb-label">Render</span><span class="rdb-val" id="rdb-render">—</span></div>

        <div class="rdb-divider"></div>
        <div class="rdb-section-label" id="rdb-car">Selected car</div>
        <div class="rdb-row"><span class="rdb-label">Mode</span><span class="rdb-val" id="rdb-drive">—</span></div>

        <div class="rdb-slider-row">
          <span class="rdb-label">Tires</span>
          <input class="rdb-slider" type="range" min="0" max="100" step="1" value="100" id="rdb-tires" title="Override tire life (debug)">
          <span class="rdb-val rdb-slider-val" id="rdb-tires-val">100%</span>
        </div>
        <div class="rdb-slider-row">
          <span class="rdb-label">Fuel</span>
          <input class="rdb-slider" type="range" min="0" max="100" step="1" value="100" id="rdb-fuel" title="Override fuel (debug)">
          <span class="rdb-val rdb-slider-val" id="rdb-fuel-val">100%</span>
        </div>
        <div class="rdb-slider-row">
          <span class="rdb-label">Damage</span>
          <input class="rdb-slider" type="range" min="0" max="100" step="1" value="0" id="rdb-damage" title="Override damage (debug)">
          <span class="rdb-val rdb-slider-val" id="rdb-damage-val">0%</span>
        </div>

        <div class="rdb-hint">backtick to toggle</div>
      </div>

      <div class="race-stage-top">
        <div class="race-control-panel" id="race-control-panel">
          <div class="race-control-title">
            <div class="rc-title-left">
              <div class="rc-tag">// Race control</div>
              <div class="rc-headline">
                <span class="rc-headline-text">Tower</span>
                <span class="rc-status" id="rc-status" hidden></span>
              </div>
            </div>
            <div class="rc-flag-slot">
              <span class="rc-flag-label">Flag</span>
              <span class="rc-flag" id="rc-flag" data-flag="none" title="Flag status">—</span>
            </div>
          </div>
          <div class="race-control-body">
            <div class="race-control-row rc-info">
              <span class="rc-stat-label">Lap</span>
              <span class="rc-stat-val" id="rc-lap">— / —</span>
            </div>
            <div class="race-control-row rc-info">
              <span class="rc-stat-label">Time</span>
              <span class="rc-stat-val" id="rc-time">0:00</span>
            </div>
            <div class="race-control-row">
              <button class="rc-btn rc-pause" id="rc-pause" title="Pause / resume the race">⏸ Pause</button>
            </div>
            <div class="race-control-row">
              <span class="rc-label">Speed</span>
              <div class="rc-speed-group" id="rc-speed-group">
                <button class="rc-btn rc-speed"        data-speed="1">1×</button>
                <button class="rc-btn rc-speed active" data-speed="2">2×</button>
                <button class="rc-btn rc-speed"        data-speed="4">4×</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="race-stage-bottom">
        <div class="race-team-panel" id="race-team-panel">
          <div class="race-team-title">
            <div class="race-team-tag" id="race-team-tag">// Team control</div>
            <div class="race-team-headline">
              <span class="race-team-meta" id="race-team-meta">—</span>
              <span class="race-team-live" id="race-team-live"><span class="race-team-live-dot"></span>LIVE</span>
            </div>
          </div>
          <div class="race-team-status" id="race-team-status" hidden></div>
          <div class="race-team-body" id="race-team-body">
            <button class="tc-btn tc-pit"      id="tc-pit"      data-hotkey="P" title="Bring this car into pit lane">PIT</button>
            <button class="tc-btn tc-push"     id="tc-push"     data-hotkey="+" title="Drive at the limit — faster laps, more wear, more fuel">PUSH</button>
            <button class="tc-btn tc-conserve" id="tc-conserve" data-hotkey="−" title="Lift early, accelerate late — stretches tires and fuel">CONSERVE</button>
          </div>
          <div class="race-team-finished" id="race-team-finished" hidden>
            <button class="race-team-garage-btn" id="race-team-garage" type="button">
              Go to garage <span class="arrow">→</span>
            </button>
          </div>
          <div class="race-team-scout" id="race-team-scout" hidden>
            <div class="scout-stats">
              <div class="scout-stat"><div class="scout-label">Damage</div><div class="scout-val" id="scout-damage">—</div></div>
              <div class="scout-stat"><div class="scout-label">Stint</div><div class="scout-val" id="scout-stint">—</div></div>
              <div class="scout-stat"><div class="scout-label">Stops</div><div class="scout-val" id="scout-stops">—</div></div>
            </div>
            <div class="scout-bars">
              <div class="scout-bar-row">
                <div class="scout-label">Aggression</div>
                <div class="scout-bar"><div class="scout-bar-fill" id="scout-aggression"></div></div>
              </div>
              <div class="scout-bar-row">
                <div class="scout-label">Skill</div>
                <div class="scout-bar"><div class="scout-bar-fill" id="scout-skill"></div></div>
              </div>
            </div>
          </div>
          <div class="race-pit-order" id="race-pit-order">
            <div class="pit-opt-row">
              <button class="pit-opt-btn active" id="pit-opt-tires"  type="button" data-key="tires">Tires</button>
              <button class="pit-opt-btn active" id="pit-opt-fuel"   type="button" data-key="fuel">Refuel</button>
              <button class="pit-opt-btn"        id="pit-opt-repair" type="button" data-key="repair">Repair</button>
              <span class="pit-eta" id="pit-eta">ETA —</span>
            </div>
            <div class="pit-slider-row" id="pit-fuel-row">
              <label class="pit-slider-label" for="pit-fuel-slider">Fuel</label>
              <input class="pit-slider" id="pit-fuel-slider" type="range" min="0" max="50" step="1" value="50">
              <span class="pit-slider-val" id="pit-fuel-val">— kg</span>
            </div>
            <div class="pit-slider-row" id="pit-repair-row">
              <label class="pit-slider-label" for="pit-repair-slider">Repair</label>
              <input class="pit-slider" id="pit-repair-slider" type="range" min="0" max="100" step="1" value="100">
              <span class="pit-slider-val" id="pit-repair-val">—%</span>
            </div>
          </div>
        </div>
      </div>

      <div class="race-stage-right">
        <div class="race-telemetry-panel" aria-live="polite">
          <div class="race-telemetry-title">
            <span>Telemetry</span>
            <span class="race-tlm-meta" id="tlm-meta">—</span>
          </div>
          <div class="race-telemetry-body">
            <div class="tlm-section tlm-driver-section" id="tlm-driver-section" hidden>
              <div class="tlm-driver-name" id="tlm-driver-name">—</div>
              <div class="tlm-driver-team" id="tlm-driver-team">—</div>
            </div>
            <div class="tlm-section">
              <div class="tlm-row big">
                <span class="label">Speed</span>
                <span class="value" id="tlm-speed">—</span>
              </div>
              <div class="tlm-row">
                <span class="label">Surface</span>
                <span class="value" id="tlm-surface">—</span>
              </div>
            </div>
            <div class="tlm-section">
              <div class="tlm-row">
                <span class="label">Pit stops</span>
                <span class="value" id="tlm-pitstops">0</span>
              </div>            
              <div class="tlm-row">
                <span class="label">Lap</span>
                <span class="value" id="tlm-lap">—</span>
              </div>
              <div class="tlm-row">
                <span class="label">Last</span>
                <span class="value" id="tlm-last">—:——.———</span>
              </div>
              <div class="tlm-row">
                <span class="label">Best</span>
                <span class="value" id="tlm-best">—:——.———</span>
              </div>
              <div class="tlm-sectors">
                <span class="sec-empty" id="tlm-s1">S1 —</span>
                <span class="sec-empty" id="tlm-s2">S2 —</span>
                <span class="sec-empty" id="tlm-s3">S3 —</span>
              </div>
            </div>
            <div class="tlm-section" data-solo-private>
              <div class="tlm-row">
                <span class="label">Throttle</span>
                <span class="value" id="tlm-throttle-val">0%</span>
              </div>
              <div class="tlm-bar throttle"><div class="tlm-bar-fill" id="tlm-throttle-fill"></div></div>
              <div class="tlm-row">
                <span class="label">Brake</span>
                <span class="value" id="tlm-brake-val">0%</span>
              </div>
              <div class="tlm-bar brake"><div class="tlm-bar-fill" id="tlm-brake-fill"></div></div>
              <div class="tlm-row">
                <span class="label">Steer</span>
                <span class="value" id="tlm-steer-val">0°</span>
              </div>
              <div class="tlm-steer-bar"><div class="tlm-steer-fill" id="tlm-steer-fill"></div></div>
            </div>
            <div class="tlm-section" data-solo-private>
              <div class="tlm-row">
                <span class="label">Lat G</span>
                <span class="value" id="tlm-latg">0.00g</span>
              </div>
              <div class="tlm-row">
                <span class="label">Long G</span>
                <span class="value" id="tlm-longg">0.00g</span>
              </div>
            </div>
            <div class="tlm-section" data-solo-private>
              <div class="tlm-row">
                <span class="label">Tire temp</span>
                <span class="value" id="tlm-tire-temp">—</span>
              </div>
              <div class="tlm-row">
                <span class="label" id="tlm-tire-label">Tires</span>
                <span class="value" id="tlm-tire-val">100%</span>
              </div>
              <div class="tlm-bar tire"><div class="tlm-bar-fill" id="tlm-tire-fill"></div></div>
              <div class="tlm-row">
                <span class="label">Fuel</span>
                <span class="value" id="tlm-fuel-val">—</span>
              </div>
              <div class="tlm-bar fuel"><div class="tlm-bar-fill" id="tlm-fuel-fill"></div></div>
              <div class="tlm-row">
                <span class="label">Damage</span>
                <span class="value" id="tlm-damage-val">0%</span>
              </div>
              <div class="tlm-bar damage"><div class="tlm-bar-fill" id="tlm-damage-fill"></div></div>
            </div>
          </div>
        </div>

        <div class="race-notifs" id="race-notifs" aria-live="polite"></div>
      </div>

      <div class="race-stage-left">
        ${backLinkHtml}
        <div class="race-ts-panel" id="race-ts-panel">
          <div class="race-ts-title">
            <span>Timing &amp; Scoring</span>
            <span class="race-ts-meta" id="race-ts-meta">—</span>
          </div>
          <div class="race-ts-list" id="race-ts-list"></div>
        </div>

        <div class="race-controls-panel collapsed" id="race-controls-panel">
          <div class="race-controls-title" id="race-controls-title">
            <span>Controls</span>
            <span class="chevron" id="race-controls-chevron">▸</span>
          </div>
          <div class="race-controls-body">
            <div><kbd>WASD</kbd>or<kbd>↑↓←→</kbd> drive</div>
            <div><kbd>Space</kbd> pause / resume — or brake while driving</div>
            <div><kbd>Esc</kbd> race menu</div>
            <div><kbd>M</kbd> take over selected car</div>
            <div><kbd>1</kbd>/<kbd>2</kbd>/<kbd>4</kbd> sim speed</div>
            <div><kbd>+</kbd> push · <kbd>-</kbd> conserve</div>
            <div><kbd>P</kbd> toggle pit · <kbd>T</kbd>/<kbd>F</kbd>/<kbd>R</kbd> tires/fuel/repair</div>
            <div><kbd>↑</kbd>/<kbd>↓</kbd> step through T&amp;S</div>
          </div>
        </div>
      </div>

      ${SHOW_SPEC_PANEL ? `
      <div class="race-spec-panel collapsed" id="race-spec-panel">
        <div class="race-spec-title" id="race-spec-title">
          <span id="race-spec-heading">Car spec</span>
          <span class="chevron" id="race-spec-chevron">▸</span>
        </div>
        <div class="race-spec-body" id="race-spec-body"></div>
      </div>
      ` : ''}
      <div class="race-curtain" id="race-curtain">
        <div class="race-curtain-card">
          <div class="race-curtain-label">// Preparing session</div>
          <div class="race-curtain-title" id="race-curtain-title">Loading track</div>
          <div class="race-curtain-status" id="race-curtain-status">Initializing…</div>
          <div class="race-curtain-bar">
            <div class="race-curtain-fill" id="race-curtain-fill"></div>
          </div>
          <div class="race-curtain-pct" id="race-curtain-pct">0%</div>
        </div>
      </div>

      <div class="race-esc-menu" id="race-esc-menu" hidden>
        <div class="race-esc-card">
          <div class="race-esc-header">
            <div class="race-esc-tag">// Race menu</div>
            <h2 class="race-esc-title">Take a <span class="accent">breath.</span></h2>
          </div>
          <div class="race-esc-actions">
            <button type="button" class="race-esc-btn primary" id="race-esc-resume">Resume <span class="race-esc-arrow">→</span></button>
            <button type="button" class="race-esc-btn" id="race-esc-restart">Restart race</button>
            <button type="button" class="race-esc-btn danger" id="race-esc-tracks">Exit</button>
          </div>
        </div>
      </div>
    </section>
  `;

  const canvas = mountEl.querySelector('#race-canvas');
  const curtainEl = mountEl.querySelector('#race-curtain');
  const curtainTitleEl  = mountEl.querySelector('#race-curtain-title');
  const curtainStatusEl = mountEl.querySelector('#race-curtain-status');
  const curtainFillEl   = mountEl.querySelector('#race-curtain-fill');
  const curtainPctEl    = mountEl.querySelector('#race-curtain-pct');

  // Loading-progress helpers. The curtain blocks the in-progress HUD
  // (which the static cache hasn't drawn yet) so the user only sees
  // the topbar nav + this card while assets decode. setProgress drives
  // a real progress bar — the heaviest step is the bg-image decode for
  // large background images (multi-megabyte data URLs decode in 1–5 s
  // on midrange hardware), which we frame with a smooth interpolator
  // so the bar moves continuously instead of staying stuck at one
  // checkpoint until the load event fires.
  let curtainPct = 0;
  let imageRampRaf = 0;
  const setProgress = (pct, label) => {
    curtainPct = Math.max(curtainPct, Math.min(100, pct));
    if (curtainFillEl) curtainFillEl.style.width = `${curtainPct}%`;
    if (curtainPctEl) curtainPctEl.textContent = `${Math.round(curtainPct)}%`;
    if (label && curtainStatusEl) curtainStatusEl.textContent = label;
  };
  const startImageRamp = (fromPct, toPct, durationMs) => {
    // Soft ramp during the bg-image decode. We don't get real byte
    // progress on data URLs, so this is a time-based estimate that
    // gives the user honest "still working" feedback. A real load that
    // beats the estimate jumps the bar forward via a later
    // setProgress; one that runs longer caps at toPct until the image
    // resolves and the next checkpoint fires.
    const startedAt = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / durationMs);
      const pct = fromPct + (toPct - fromPct) * t;
      setProgress(pct);
      if (t < 1 && imageRampRaf) imageRampRaf = requestAnimationFrame(tick);
    };
    imageRampRaf = requestAnimationFrame(tick);
  };
  const stopImageRamp = () => {
    if (imageRampRaf) cancelAnimationFrame(imageRampRaf);
    imageRampRaf = 0;
  };
  const dropCurtain = () => {
    stopImageRamp();
    if (!curtainEl) return;
    curtainEl.classList.add('is-revealing');
    // Once the fade finishes, remove it from the DOM so it isn't an
    // overlay swallowing input forever.
    curtainEl.addEventListener('transitionend', () => curtainEl.remove(), { once: true });
  };

  let sim = null;
  let hudRaf = 0;
  let cancelled = false;

  // Window-level handlers are declared here at function scope so the
  // cleanup function (defined after the try block) can actually see
  // them. Earlier these were `const` inside the try block — block-
  // scoping meant `removeEventListener` got an `undefined` reference
  // and silently failed, which left the beforeunload prompt firing
  // on later pages after the user had navigated away.
  let onDebugKey = null;
  let onBeforeUnload = null;
  let onPauseKey = null;
  let onShortcutKey = null;
  let onEscKey = null;
  // Original topbar markup, captured on mount and restored on teardown
  // so leaving the race route puts the global Pit Lane / Tracks /
  // Roster links back. Null = nav element was missing at mount.
  let prevNavHtml = null;

  // Per-car fuel-burn tracker. Snapshots fuel + racing-line arc at the
  // start of each lap so the laps-left readout can project from the
  // car's ACTUAL burn this lap (measurement) instead of the static
  // burn-rate × duty-cycle × lap-time-guess heuristic. Map<carIdx, {
  // lap, lapStartFuel, lapStartArc, lastLapFuelBurn }>.
  const fuelTrackers = new Map();

  // Hoisted teardown so abandonment-bail paths can return it. The
  // router's superseded-cleanup branch invokes whatever the page
  // function returns when a later navigation has overtaken us — if we
  // bailed before mounting the modal but after creating sim, we still
  // need sim.unmount() to fire and the window listeners to come off.
  const teardown = () => {
    cancelled = true;
    cancelAnimationFrame(hudRaf);
    stopImageRamp();
    sim?.unmount();
    if (onDebugKey)     window.removeEventListener('keydown', onDebugKey);
    if (onPauseKey)     window.removeEventListener('keydown', onPauseKey);
    if (onShortcutKey)  window.removeEventListener('keydown', onShortcutKey);
    if (onEscKey)       window.removeEventListener('keydown', onEscKey);
    if (onBeforeUnload) window.removeEventListener('beforeunload', onBeforeUnload);
    // Put the global topbar nav back. The race page swapped it for
    // a single Menu button on mount; the next route expects the
    // Pit Lane / Tracks / Roster links it had before.
    if (prevNavHtml != null) {
      const navEl = document.getElementById('nav');
      if (navEl) navEl.innerHTML = prevNavHtml;
    }
    clearNavigationGuard();
  };

  try {
    setProgress(5, fromSession ? 'Reading staged track…' : 'Fetching track data…');
    let track;
    if (fromSession) {
      const data = getEditorSessionTrack();
      if (!data) throw new Error('No session-staged track found. Click Test Track from the editor to stage one.');
      const result = loadTrack(data);
      track = result.track;
    } else {
      track = await getTrack(trackId);
    }
    if (cancelled || !canvas.isConnected) return teardown;
    if (curtainTitleEl) curtainTitleEl.textContent = track.name || 'Loading track';
    setProgress(20, 'Building track geometry…');

    // Roster handoff. The picker (race-setup.js) writes the selected
    // team list into sessionStorage; we resolve it into per-car
    // entries here. If nothing was picked (direct-link race or
    // editor's Test Track flow), fall back to mountSimulator's
    // default gridSize behavior.
    const setup = !fromSession ? getRaceSetup() : null;
    const entries = setup && setup.trackId === trackId
      ? await buildEntriesFromSetup(setup)
      : null;
    if (cancelled || !canvas.isConnected) return teardown;
    // Setup is intentionally NOT cleared after read so a page refresh
    // re-mounts the same grid instead of dropping to a 1-car default.
    // Track-mismatch protection lives in the read above (we only use
    // the setup when its trackId matches the URL), so leaving it in
    // sessionStorage doesn't bleed into races for a different track.
    setProgress(35, 'Initializing simulator…');
    sim = mountSimulator(canvas, track, entries ? { entries } : {});

    // Sim sits in raceState 'pre-race' until the user clicks Start
    // on the overlay. Physics is gated on the phase in main.js, so
    // there's no need to setPaused — the loop just won't tick.
    // Render still runs every frame so the grid is visible.
    //
    // Wait for static-layer assets (bg image) before revealing the
    // canvas; without this the loading panel hides before the bg
    // image has decoded, producing a brief flash of empty canvas.
    // Most of the wait is image decode for tracks with a multi-MB
    // background — kick off a smooth time-based ramp so the bar
    // moves continuously instead of stalling at one number.
    if (track.backgroundImage?.dataUrl) {
      setProgress(40, 'Decoding background image…');
      startImageRamp(40, 80, 4000);
    } else {
      setProgress(80, 'Initializing simulator…');
    }
    await sim.ready;
    stopImageRamp();
    if (cancelled || !canvas.isConnected) return teardown;
    setProgress(85, 'Spawning grid…');

    // Resolve the setup bundle into the shape runPreRaceOverlay wants.
    // Solo driver picks are by driverId on the setup page (cars don't
    // exist there yet); cars carry that ID through, so a linear scan
    // maps it to a car index. Defaults if no setup or no match: god
    // mode, default lap count.
    let preraceOpts = { raceMode: 'god', soloCarIdx: null, totalLaps: DEFAULT_RACE_LAPS };
    if (setup && setup.trackId === trackId) {
      const totalLaps = Number.isFinite(setup.totalLaps) && setup.totalLaps > 0
        ? Math.floor(setup.totalLaps) : DEFAULT_RACE_LAPS;
      let soloCarIdx = null;
      if (setup.raceMode === 'solo' && setup.soloDriverId) {
        soloCarIdx = sim.world.cars.findIndex(c => c.driverId === setup.soloDriverId);
        if (soloCarIdx < 0) soloCarIdx = null;
      }
      preraceOpts = {
        raceMode: setup.raceMode === 'solo' && soloCarIdx != null ? 'solo' : 'god',
        soloCarIdx,
        totalLaps,
      };
    }
    // Solo mode: focus the player's car as soon as the world is up
    // so the grid is visible from THEIR perspective during the
    // pre-race overlay + lights sequence, not whatever index 0 is.
    // The lights-out callback re-selects to be safe (idempotent).
    if (preraceOpts.raceMode === 'solo' && Number.isFinite(preraceOpts.soloCarIdx)) {
      sim.selectCar(preraceOpts.soloCarIdx);
    }
    // Abandonment guard. If the user navigated away while we were
    // suspended on `await sim.ready`, mountEl has been wiped and a
    // different page is mounted there — appending the modal now would
    // make it pop up on the wrong page (and the lights gantry would
    // count down over whatever page the user is currently on). The
    // canvas is the most reliable DOM-truth check: innerHTML='' on the
    // mount detaches it, so isConnected flips to false. The router's
    // superseded-cleanup path will still call our teardown to free the
    // sim and listeners.
    if (!canvas.isConnected) return teardown;
    setProgress(100, 'Ready');
    runPreRaceOverlay(mountEl, sim, preraceOpts);
    // Modal is in the DOM — fade the curtain so the user sees the
    // Start Race Session card with the grid visible behind it.
    dropCurtain();

    // Telemetry panel cells.
    const tlmMetaEl    = mountEl.querySelector('#tlm-meta');
    const tlmDrvSecEl  = mountEl.querySelector('#tlm-driver-section');
    const tlmDrvNameEl = mountEl.querySelector('#tlm-driver-name');
    const tlmDrvTeamEl = mountEl.querySelector('#tlm-driver-team');
    const tlmSpeedEl   = mountEl.querySelector('#tlm-speed');
    const tlmSurfEl    = mountEl.querySelector('#tlm-surface');
    const tlmLapEl     = mountEl.querySelector('#tlm-lap');
    const tlmLastEl    = mountEl.querySelector('#tlm-last');
    const tlmBestEl    = mountEl.querySelector('#tlm-best');
    const tlmS1El      = mountEl.querySelector('#tlm-s1');
    const tlmS2El      = mountEl.querySelector('#tlm-s2');
    const tlmS3El      = mountEl.querySelector('#tlm-s3');
    const tlmThrValEl  = mountEl.querySelector('#tlm-throttle-val');
    const tlmThrFillEl = mountEl.querySelector('#tlm-throttle-fill');
    const tlmBrkValEl  = mountEl.querySelector('#tlm-brake-val');
    const tlmBrkFillEl = mountEl.querySelector('#tlm-brake-fill');
    const tlmStrValEl  = mountEl.querySelector('#tlm-steer-val');
    const tlmStrFillEl = mountEl.querySelector('#tlm-steer-fill');
    const tlmLatGEl    = mountEl.querySelector('#tlm-latg');
    const tlmLongGEl   = mountEl.querySelector('#tlm-longg');
    const tlmTireLblEl = mountEl.querySelector('#tlm-tire-label');
    const tlmTireValEl = mountEl.querySelector('#tlm-tire-val');
    const tlmTireFillEl= mountEl.querySelector('#tlm-tire-fill');
    const tlmTireTempEl= mountEl.querySelector('#tlm-tire-temp');
    const tlmFuelValEl = mountEl.querySelector('#tlm-fuel-val');
    const tlmFuelFillEl= mountEl.querySelector('#tlm-fuel-fill');
    const tlmDmgValEl  = mountEl.querySelector('#tlm-damage-val');
    const tlmDmgFillEl = mountEl.querySelector('#tlm-damage-fill');
    const tlmPitStopsEl= mountEl.querySelector('#tlm-pitstops');
    // Solo-mode telemetry filter targets — toggled per-frame in tickHud.
    const tlmBodyEl    = mountEl.querySelector('.race-telemetry-body');
    const teamPanelEl  = mountEl.querySelector('#race-team-panel');

    // Debug-mode overlay: backtick toggles. When on, perf metrics show
    // and the solo-private gate is bypassed so any selected car's
    // tires / fuel / temp / damage are visible — useful for watching
    // the AI's strategic state without giving up solo mode.
    const dbgPanelEl   = mountEl.querySelector('#race-debug-panel');
    const dbgFpsEl     = mountEl.querySelector('#rdb-fps');
    const dbgFrameEl   = mountEl.querySelector('#rdb-frame');
    const dbgTickEl    = mountEl.querySelector('#rdb-tick');
    const dbgRenderEl  = mountEl.querySelector('#rdb-render');
    const dbgCarEl     = mountEl.querySelector('#rdb-car');
    const dbgDriveEl   = mountEl.querySelector('#rdb-drive');
    const dbgTiresEl   = mountEl.querySelector('#rdb-tires');
    const dbgTiresValEl= mountEl.querySelector('#rdb-tires-val');
    const dbgFuelEl    = mountEl.querySelector('#rdb-fuel');
    const dbgFuelValEl = mountEl.querySelector('#rdb-fuel-val');
    const dbgDmgEl     = mountEl.querySelector('#rdb-damage');
    const dbgDmgValEl  = mountEl.querySelector('#rdb-damage-val');
    let debugMode = false;
    onDebugKey = (e) => {
      // Ignore when typing into an input/textarea so backtick can be
      // used in those contexts without flipping debug.
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key !== '`') return;
      debugMode = !debugMode;
      if (dbgPanelEl) dbgPanelEl.hidden = !debugMode;
    };
    window.addEventListener('keydown', onDebugKey);

    // Debug condition overrides. Each slider mutates the selected car's
    // condition directly — physics keeps wearing/burning from there as
    // normal, so the overrides act as setpoints, not freezes. The
    // active-element guard mirrors the editor pattern: the per-frame
    // HUD reads condition → slider value, but skips that write while
    // the user is dragging the slider, so the input doesn't fight the
    // user's drag (CLAUDE.md flags this as the load-bearing rule for
    // editor sliders, applies here for the same reason).
    const setSelectedCondition = (mutate) => {
      const car = sim?.world.cars[sim.world.selectedCarIdx];
      if (!car?.condition) return;
      mutate(car);
    };
    if (dbgTiresEl) dbgTiresEl.addEventListener('input', () => {
      const v = Math.max(0, Math.min(100, parseFloat(dbgTiresEl.value))) / 100;
      setSelectedCondition(car => {
        car.condition.tireLife = v;
        // Setting tire life back above zero clears the blown flag so
        // the car's grip recovers immediately. tireZeroTimeS gates the
        // "running on the carcass" damage; reset that too so we don't
        // keep accumulating run-flat damage right after a fix.
        if (v > 0) {
          car.condition.blownTires = false;
          car.condition.tireZeroTimeS = 0;
        }
      });
      if (dbgTiresValEl) dbgTiresValEl.textContent = `${Math.round(v * 100)}%`;
    });
    if (dbgFuelEl) dbgFuelEl.addEventListener('input', () => {
      const pct = Math.max(0, Math.min(100, parseFloat(dbgFuelEl.value))) / 100;
      setSelectedCondition(car => {
        const cap = car.spec?.fuelCapacity ?? car.condition.fuel;
        car.condition.fuel = cap * pct;
      });
      if (dbgFuelValEl) dbgFuelValEl.textContent = `${Math.round(pct * 100)}%`;
    });
    if (dbgDmgEl) dbgDmgEl.addEventListener('input', () => {
      const v = Math.max(0, Math.min(100, parseFloat(dbgDmgEl.value))) / 100;
      setSelectedCondition(car => {
        car.condition.damage = v;
      });
      if (dbgDmgValEl) dbgDmgValEl.textContent = `${Math.round(v * 100)}%`;
    });

    // Controls panel — collapsible, default collapsed.
    const ctrlPanelEl   = mountEl.querySelector('#race-controls-panel');
    const ctrlTitleEl   = mountEl.querySelector('#race-controls-title');
    const ctrlChevEl    = mountEl.querySelector('#race-controls-chevron');
    ctrlTitleEl.addEventListener('click', () => {
      ctrlPanelEl.classList.toggle('collapsed');
      ctrlChevEl.textContent = ctrlPanelEl.classList.contains('collapsed') ? '▸' : '▾';
    });

    // Race Control panel — pause/play + speed multiplier + flag/lap/time.
    const rcPauseEl   = mountEl.querySelector('#rc-pause');
    const rcSpeedGrp  = mountEl.querySelector('#rc-speed-group');
    const rcFlagEl    = mountEl.querySelector('#rc-flag');
    const rcStatusEl  = mountEl.querySelector('#rc-status');
    const rcLapEl     = mountEl.querySelector('#rc-lap');
    const rcTimeEl    = mountEl.querySelector('#rc-time');
    const refreshPauseBtn = () => {
      const paused = sim.isPaused();
      rcPauseEl.textContent = paused ? '▶ Resume' : '⏸ Pause';
      rcPauseEl.classList.toggle('paused', paused);
    };
    rcPauseEl.addEventListener('click', () => {
      sim.setPaused(!sim.isPaused());
      refreshPauseBtn();
    });
    // Refresh / tab-close warning during active racing. Browsers don't
    // let us actually block refresh — they only let us prompt. Phase
    // gating means the prompt only fires when there's race progress
    // worth losing (mid-race, mid-cooldown). Pre-race / finished —
    // refresh away, nothing's at stake.
    const isRaceUnderway = () => {
      const phase = sim?.world?.raceState?.phase;
      return phase === 'racing' || phase === 'cooldown';
    };
    onBeforeUnload = (e) => {
      if (!isRaceUnderway()) return;
      e.preventDefault();
      e.returnValue = '';   // required by some browsers to show the prompt
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    // Same protection for in-app navigation: clicking Roster / Tracks /
    // Pit Lane / brand link in the topbar fires a hashchange that the
    // router checks against this guard. Returning false reverts the URL.
    setNavigationGuard(() => {
      if (!isRaceUnderway()) return true;
      return window.confirm('A race is in progress. Leave anyway? Your progress will be lost.');
    });

    // Spacebar toggles pause — but only when nobody is driving
    // manually. The keyboard controller in simulator/controllers.js
    // reads keys[' '] as brake, so if a car is in manual mode we leave
    // space alone. Also skip when typing into inputs so a space inside
    // a text field doesn't pause the race.
    onPauseKey = (e) => {
      if (e.code !== 'Space') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (sim.world.cars.some(c => c.controllerType === 'manual')) return;
      e.preventDefault();
      sim.setPaused(!sim.isPaused());
      refreshPauseBtn();
    };
    window.addEventListener('keydown', onPauseKey);

    // Esc menu — pause/restart/exit affordances. Keeps the player from
    // having to know about the pause shortcut + nav guard interaction
    // when they just want to bail. The menu pauses the sim while open
    // and restores the prior pause state on dismiss, so the rest of
    // the page (HUD, notifs) keeps reading consistent state.
    const escMenuEl     = mountEl.querySelector('#race-esc-menu');
    const escResumeBtn  = mountEl.querySelector('#race-esc-resume');
    const escRestartBtn = mountEl.querySelector('#race-esc-restart');
    const escTracksBtn  = mountEl.querySelector('#race-esc-tracks');
    let escMenuPrevPaused = false;
    const closeEscMenu = () => {
      if (!escMenuEl || escMenuEl.hidden) return;
      escMenuEl.hidden = true;
      // Only unpause if WE paused it — leave alone if the user paused
      // before opening the menu.
      if (!escMenuPrevPaused && sim.isPaused()) {
        sim.setPaused(false);
        refreshPauseBtn();
      }
    };
    const openEscMenu = () => {
      if (!escMenuEl || !escMenuEl.hidden) return;
      escMenuPrevPaused = sim.isPaused();
      if (!escMenuPrevPaused) {
        sim.setPaused(true);
        refreshPauseBtn();
      }
      escMenuEl.hidden = false;
    };
    // Topbar nav swap. The race page is the only place where the
    // global Pit Lane / Tracks / Roster links don't belong (they're
    // navigations away from an in-progress sim — the Esc menu is the
    // proper exit ramp), so we replace them with a single MENU button
    // that opens the same modal. Original markup is restored on
    // teardown so other routes get the standard nav back. Stored as
    // a string rather than a node ref because innerHTML returns the
    // serialized form anyway and a string is easier to restore.
    // Suppress the Esc menu when the prerace card, post-race results,
    // or starting-lights gantry is up — they already own the screen
    // and stacking another modal on top is just a duplicate path.
    const isBlockingModalOpen = () => !!(
         mountEl.querySelector('.race-prerace')
      || mountEl.querySelector('.race-results')
      || mountEl.querySelector('.race-lights')
    );

    const topbarNavEl = document.getElementById('nav');
    if (topbarNavEl) {
      prevNavHtml = topbarNavEl.innerHTML;
      topbarNavEl.innerHTML = '<button type="button" class="nav-menu-btn" id="race-nav-menu">Menu</button>';
      topbarNavEl.querySelector('#race-nav-menu')?.addEventListener('click', () => {
        if (isBlockingModalOpen()) return;
        // Toggle: clicking Menu again while open dismisses it, same
        // behavior as pressing Esc.
        if (escMenuEl?.hidden) openEscMenu();
        else closeEscMenu();
      });
    }

    escResumeBtn?.addEventListener('click', closeEscMenu);

    // Shared restart pipeline. Called from both the Esc menu's Restart
    // button and the post-race results modal's Restart button — same
    // semantics either way: wipe the in-progress race, drop pause,
    // remove any stale overlays, re-show the prerace card with the
    // existing setup carried over.
    const triggerRestart = () => {
      sim.restartRace();
      const rs = sim.world.raceState;
      const opts = {
        raceMode:   rs?.raceMode ?? 'god',
        soloCarIdx: rs?.soloCarIdx ?? null,
        totalLaps:  rs?.totalLaps ?? DEFAULT_RACE_LAPS,
      };
      escMenuPrevPaused = false;
      sim.setPaused(false);
      refreshPauseBtn();
      if (escMenuEl) escMenuEl.hidden = true;
      mountEl.querySelector('.race-results')?.remove();
      runPreRaceOverlay(mountEl, sim, opts);
    };
    escRestartBtn?.addEventListener('click', triggerRestart);
    escTracksBtn?.addEventListener('click', () => {
      // Clicking this button IS the confirmation — the user has
      // already opened the menu and chosen to leave the race, so the
      // router-level "race in progress, leave anyway?" prompt would
      // be a redundant second click. Clear the guard before navigating.
      clearNavigationGuard();
      window.location.hash = '#/tracks';
    });
    // Esc keydown — toggle. Skipped while typing in inputs so a press
    // inside a text field doesn't pop the menu, and while a race-flow
    // modal owns the screen (prerace / results / starting lights).
    // The latter avoids "Esc opens the menu over the Start Race
    // Session card" — same gate the Menu button uses.
    onEscKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      // If the Esc menu is already open, allow Esc to close it even
      // if a blocking modal landed on top of it (defensive — shouldn't
      // happen, but means the user can't get stuck).
      if (escMenuEl.hidden && isBlockingModalOpen()) return;
      e.preventDefault();
      if (escMenuEl.hidden) openEscMenu();
      else closeEscMenu();
    };
    window.addEventListener('keydown', onEscKey);
    // Click on backdrop (outside the card) closes the menu — common
    // modal convention.
    escMenuEl?.addEventListener('click', (e) => {
      if (e.target === escMenuEl) closeEscMenu();
    });

    // Race-control keyboard shortcuts. Same gates as the pause key —
    // disabled while typing in an input AND while any car is in
    // manual mode (so the keys don't fight driving inputs).
    //
    // 1 / 2 / 4         — sim speed (any phase, any selected car).
    // + (or =)          — toggle Push on selected car (cancels Conserve).
    // - (or _)          — toggle Conserve on selected car (cancels Push).
    // P                 — toggle pit request on selected car.
    // T / F / R         — toggle tires / fuel / repair in the pit
    //                     order, but only while the order is editable
    //                     (matches the visible pit-order panel's gate:
    //                     racing+requested OR pitting before the
    //                     speed-zone board).
    //
    // Solo mode: the targeted car defaults to selectedCarIdx, but
    // strategy keys (everything except speed) only fire when the
    // selected car is the player's own car. Same rule as the team-
    // control panel's scouting view — the player can't remote-control
    // a rival team's strategy by spectating their car.
    onShortcutKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (sim.world.cars.some(c => c.controllerType === 'manual')) return;
      // Modifier-key combos belong to the browser / OS — never hijack.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key;

      // Sim speed — match the visible button group's active state.
      const speedMap = { '1': 1, '2': 2, '4': 4 };
      if (Object.prototype.hasOwnProperty.call(speedMap, key)) {
        const mul = speedMap[key];
        sim.setSpeed(mul);
        rcSpeedGrp.querySelectorAll('button').forEach(b => {
          b.classList.toggle('active', Number(b.dataset.speed) === mul);
        });
        e.preventDefault();
        return;
      }

      // Up/Down arrows — step the selected driver through the timing &
      // scoring order. Mirrors what clicking a T&S row does (selectCar
      // + UI refresh). Sits inside the same no-manual-car gate as the
      // strategy hotkeys above so arrow-key throttle/brake in a manual
      // session isn't fighting standings navigation. Wraps from leader
      // to last (and vice versa) so a long field can be cycled without
      // scrolling back to the start.
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const standings = computeStandings();
        const N = standings.length;
        if (N === 0) return;
        const retiredCars = sim.world.raceState?.retiredCars;
        const isRetired = (idx) =>
          retiredCars instanceof Map && retiredCars.has(idx);
        const currentIdx = sim.world.selectedCarIdx;
        let pos = standings.findIndex(s => s.idx === currentIdx);
        if (pos < 0) pos = 0;
        const step = key === 'ArrowUp' ? -1 : 1;
        // Walk past retired entries until we land on an in-race car.
        // The full lap around the field guards against the all-DNF
        // edge case (everyone retired) so we don't loop forever.
        let next = pos;
        for (let i = 0; i < N; i++) {
          next = (next + step + N) % N;
          if (!isRetired(standings[next].idx)) break;
        }
        if (isRetired(standings[next].idx)) {
          // Whole field is out — there's nothing to select. Bail.
          e.preventDefault();
          return;
        }
        sim.selectCar(standings[next].idx);
        updateTimingPanel();
        renderPanel(false);
        e.preventDefault();
        return;
      }

      // Strategy shortcuts — gated to the player's own car in solo mode.
      const idx = sim.world.selectedCarIdx;
      const rs  = sim.world.raceState;
      if (rs?.raceMode === 'solo' && idx !== rs.soloCarIdx) return;
      const car = sim.world.cars[idx];
      if (!car) return;

      // Drive modes. Plus toggles Push (off → push, push → off),
      // minus toggles Conserve. setDriveMode replaces, so flipping to
      // 'push' from 'conserve' implicitly cancels conserve.
      if (key === '+' || key === '=') {
        const current = sim.getDriveMode(idx);
        sim.setDriveMode(idx, current === 'push' ? 'normal' : 'push');
        refreshTeamPanel();
        e.preventDefault();
        return;
      }
      if (key === '-' || key === '_') {
        const current = sim.getDriveMode(idx);
        sim.setDriveMode(idx, current === 'conserve' ? 'normal' : 'conserve');
        refreshTeamPanel();
        e.preventDefault();
        return;
      }

      const ps = car.pitState;

      // Pit request toggle — same as clicking the PIT button. Only
      // racing-phase cars can be requested; mid-pit cancellation
      // isn't supported.
      if (key === 'p' || key === 'P') {
        if (!sim.world.geometry.pitRoute) return;
        if (ps.phase !== 'racing') return;
        if (ps.requested) sim.cancelPit(idx);
        else              sim.requestPit(idx);
        refreshTeamPanel();
        e.preventDefault();
        return;
      }

      // Pit-order toggles — gated on the order being editable, same
      // gate as the visible toggle buttons in refreshTeamPanel.
      const orderKey = (key === 't' || key === 'T') ? 'tires'
                     : (key === 'f' || key === 'F') ? 'fuel'
                     : (key === 'r' || key === 'R') ? 'repair'
                     : null;
      if (!orderKey) return;
      if (!sim.world.geometry.pitRoute) return;
      const speedZone = sim.world.geometry.pitSpeedZone;
      const carInPitZone = ps.phase === 'pitting' && (
        speedZone == null || car.derived.lineArc >= speedZone.startArc
      );
      const orderEditable =
           (ps.phase === 'racing'  && ps.requested)
        || (ps.phase === 'pitting' && !carInPitZone);
      if (!orderEditable) return;
      const order = sim.getPitOrder(idx);
      if (!order) return;
      sim.setPitOrder(idx, { [orderKey]: !order[orderKey] });
      refreshTeamPanel();
      e.preventDefault();
    };
    window.addEventListener('keydown', onShortcutKey);

    rcSpeedGrp.addEventListener('click', e => {
      const btn = e.target.closest('button[data-speed]');
      if (!btn) return;
      const mul = Number(btn.dataset.speed);
      sim.setSpeed(mul);
      rcSpeedGrp.querySelectorAll('button').forEach(b => {
        b.classList.toggle('active', Number(b.dataset.speed) === mul);
      });
    });

    // Race-control top-bar refresh — flag color/text, leader lap of
    // total, elapsed race time. Closes over the rc element refs above
    // so the tickHud just calls it once per frame.
    //
    // Flag rules:
    //   pre-race        — "—" (no flag yet)
    //   racing          — yellow if raceState.flag === 'yellow'
    //                     (future safety-car wiring); white if the
    //                     leader is on the final lap; otherwise green.
    //   cooldown/done   — checkered.
    function fmtRaceTime(secs) {
      if (!Number.isFinite(secs) || secs < 0) return '0:00';
      const total = Math.floor(secs);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total - h * 3600) / 60);
      const s = total - h * 3600 - m * 60;
      const ss = String(s).padStart(2, '0');
      if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
      return `${m}:${ss}`;
    }
    function leaderLapNumber(world) {
      let best = 0;
      for (const c of world.cars) {
        const lap = c.lapState?.lapNumber ?? 0;
        if (lap > best) best = lap;
      }
      return best;
    }
    function flagFor(rs, leaderLap) {
      if (!rs) return 'none';
      // Red overrides everything — once it's flying the session is
      // stopped (all-DNF abandon today, future safety stoppages too),
      // and "checkered" would misrepresent that the race actually
      // completed its scheduled distance.
      if (rs.flag === 'red') return 'red';
      if (rs.phase === 'cooldown' || rs.phase === 'finished') return 'checkered';
      if (rs.phase !== 'racing') return 'none';
      // Final lap = leader is currently RUNNING totalLaps. lapNumber
      // increments to N when the leader STARTS lap N, so they're on
      // their final tour when lapNumber === totalLaps. Race finishing
      // under caution shows BOTH white and yellow — the field is
      // closing out the last tour at pace-car speed (real motorsport
      // shows the two flags side-by-side from the starter's stand for
      // exactly this reason).
      const isLastLap = leaderLap === rs.totalLaps && rs.totalLaps > 0;
      if (rs.flag === 'yellow') return isLastLap ? 'white-yellow' : 'yellow';
      if (isLastLap) return 'white';
      return 'green';
    }
    const FLAG_LABEL = {
      none:          '—',
      green:         'GREEN',
      yellow:        'YELLOW',
      red:           'RED',
      white:         'WHITE',
      'white-yellow':'WHITE + YELLOW',
      checkered:     'CHECKERED',
    };
    function updateRaceControl(rs, world) {
      const leaderLap = leaderLapNumber(world);
      const flag      = flagFor(rs, leaderLap);
      if (rcFlagEl && rcFlagEl.dataset.flag !== flag) {
        rcFlagEl.dataset.flag    = flag;
        rcFlagEl.textContent     = FLAG_LABEL[flag] || '—';
        // Pulse the pill on every flag change so the player notices
        // the transition even when their attention is elsewhere on the
        // canvas. Removing then re-adding the class restarts the CSS
        // animation if a second change lands during the previous pulse
        // (the prior approach of only adding skipped re-trigger).
        rcFlagEl.classList.remove('flash');
        // Force reflow so removing/adding `flash` in the same tick
        // restarts the animation rather than being coalesced.
        // eslint-disable-next-line no-unused-expressions
        rcFlagEl.offsetWidth;
        rcFlagEl.classList.add('flash');
      }
      // Status pill — surfaces the green-next-time-by warn alongside
      // the yellow flag. Toast notifications fade after a few seconds;
      // the race-control pill persists until the caution lifts so the
      // player has a permanent reminder. Cleared whenever caution
      // isn't active or warn hasn't fired (the latter resets on a
      // re-extension).
      if (rcStatusEl) {
        // Only meaningful while racing — once the leader takes the
        // checkered (cooldown/finished), there is no "next time by"
        // because the race is over, even if the caution machine still
        // technically reads `warned` from the last lap.
        const showWarn = rs?.phase === 'racing'
          && rs?.caution?.active
          && rs?.caution?.warned;
        if (showWarn) {
          if (rcStatusEl.textContent !== 'GREEN NEXT TIME BY') {
            rcStatusEl.textContent = 'GREEN NEXT TIME BY';
          }
          if (rcStatusEl.hidden) rcStatusEl.hidden = false;
        } else if (!rcStatusEl.hidden) {
          rcStatusEl.hidden = true;
          rcStatusEl.textContent = '';
        }
      }
      // Lap display. Leader's lap clamped to [1, totalLaps] once the
      // race is underway — pre-race shows em-dash, post-race holds
      // at totalLaps so the panel doesn't read "21 / 20" after the
      // checkered (leader.lapNumber goes one past the limit).
      if (rcLapEl) {
        const totalLaps = rs?.totalLaps ?? 0;
        if (!rs || rs.phase === 'pre-race' || totalLaps <= 0) {
          rcLapEl.textContent = '— / —';
        } else {
          const display = Math.min(Math.max(leaderLap, 1), totalLaps);
          rcLapEl.textContent = `${display} / ${totalLaps}`;
        }
      }
      // Race time = simTime - startedAt. Pre-race holds at 0:00,
      // post-checkered freezes at the leader's finish time so the
      // clock stops ticking visually even though the sim is still
      // running cool-down laps.
      if (rcTimeEl) {
        let secs = 0;
        if (rs?.startedAt != null) {
          const end = rs.finishedAt != null ? rs.finishedAt : world.simTime;
          secs = end - rs.startedAt;
        }
        rcTimeEl.textContent = fmtRaceTime(secs);
      }
    }
    // Initial paint so the panel doesn't read "0:00 — / —" with no
    // styling until the first hud tick lands.
    updateRaceControl(sim.world.raceState, sim.world);

    // ---- team control panel ----
    // Per-car commands targeting the currently-selected car. The panel
    // is the manager-mode handoff into the simulator; today only PIT
    // does work (request a stop → AI peels off at the entry, drives
    // the pit route, rejoins on exit). PUSH / PASS / DEFEND / CONSERVE
    // are placeholders until the strategy layer lands.
    const teamMetaEl   = mountEl.querySelector('#race-team-meta');
    const teamBodyEl   = mountEl.querySelector('#race-team-body');
    const teamStatusEl = mountEl.querySelector('#race-team-status');
    const teamTagEl    = mountEl.querySelector('#race-team-tag');
    const teamLiveEl   = mountEl.querySelector('#race-team-live');
    const teamScoutEl    = mountEl.querySelector('#race-team-scout');
    const teamFinishedEl = mountEl.querySelector('#race-team-finished');
    const teamGarageEl   = mountEl.querySelector('#race-team-garage');
    const scoutDamageEl     = mountEl.querySelector('#scout-damage');
    const scoutStintEl      = mountEl.querySelector('#scout-stint');
    const scoutStopsEl      = mountEl.querySelector('#scout-stops');
    const scoutAggressionEl = mountEl.querySelector('#scout-aggression');
    const scoutSkillEl      = mountEl.querySelector('#scout-skill');
    const tcPitEl      = mountEl.querySelector('#tc-pit');
    const tcPushEl     = mountEl.querySelector('#tc-push');
    const tcConserveEl = mountEl.querySelector('#tc-conserve');
    const hasPitRoute = !!sim.world.geometry.pitRoute;
    if (!hasPitRoute) {
      tcPitEl.disabled = true;
      tcPitEl.title = 'No pit lane authored on this track';
    }
    tcPitEl.addEventListener('click', () => {
      const idx = sim.world.selectedCarIdx;
      const car = sim.world.cars[idx];
      if (!car) return;
      // Toggle: if this car already has a pending request, treat the
      // click as a cancel. Once committed ('pitting') the click is a
      // no-op — bailing mid-pit needs a different rejoin path.
      if (car.pitState.requested) sim.cancelPit(idx);
      else                         sim.requestPit(idx);
      refreshTeamPanel();
    });

    // Pit-order toggle buttons — each click flips its flag in the
    // selected car's order. Active state is shown by the .active
    // class (filled accent color); inactive is muted. The row hides
    // entirely once the car is committed to pit, since the order is
    // locked at that point and the empty checkboxes were just noise.
    const pitOrderEl     = mountEl.querySelector('#race-pit-order');
    const pitOptTiresEl  = mountEl.querySelector('#pit-opt-tires');
    const pitOptFuelEl   = mountEl.querySelector('#pit-opt-fuel');
    const pitOptRepairEl = mountEl.querySelector('#pit-opt-repair');
    const pitEtaEl       = mountEl.querySelector('#pit-eta');
    const pitFuelRowEl   = mountEl.querySelector('#pit-fuel-row');
    const pitFuelSliderEl= mountEl.querySelector('#pit-fuel-slider');
    const pitFuelValEl   = mountEl.querySelector('#pit-fuel-val');
    const pitRepairRowEl = mountEl.querySelector('#pit-repair-row');
    const pitRepairSliderEl = mountEl.querySelector('#pit-repair-slider');
    const pitRepairValEl = mountEl.querySelector('#pit-repair-val');
    if (!hasPitRoute) {
      pitOptTiresEl.disabled = true;
      pitOptFuelEl.disabled  = true;
      pitOptRepairEl.disabled= true;
      pitFuelSliderEl.disabled = true;
      pitRepairSliderEl.disabled = true;
    }
    const wirePitOpt = (el, key) => {
      el.addEventListener('click', () => {
        const idx   = sim.world.selectedCarIdx;
        const order = sim.getPitOrder(idx);
        if (!order) return;
        sim.setPitOrder(idx, { [key]: !order[key] });
        refreshTeamPanel();
      });
    };
    wirePitOpt(pitOptTiresEl,  'tires');
    wirePitOpt(pitOptFuelEl,   'fuel');
    wirePitOpt(pitOptRepairEl, 'repair');

    // Sliders for explicit refuel-amount and damage-repair-amount.
    // The 'input' event fires continuously during drag — we call
    // setPitOrder live so the ETA readout tracks the slider position
    // without waiting for release. Slider values are in display units
    // (kg for fuel, percent 0..100 for repair); we convert percent to
    // 0..1 fraction for the schema.
    pitFuelSliderEl.addEventListener('input', () => {
      const idx = sim.world.selectedCarIdx;
      const kg  = Number(pitFuelSliderEl.value);
      sim.setPitOrder(idx, { fuelKg: kg });
      refreshTeamPanel();
    });
    pitRepairSliderEl.addEventListener('input', () => {
      const idx  = sim.world.selectedCarIdx;
      const pct  = Number(pitRepairSliderEl.value);
      sim.setPitOrder(idx, { repairFrac: pct / 100 });
      refreshTeamPanel();
    });

    // Drive-mode toggles. Push and Conserve are mutually exclusive
    // pace dials — clicking one cancels the other and switches.
    // Clicking the active mode again returns the car to normal.
    // Pass / Defend remain placeholders until tactical modes land.
    const wireDriveMode = (el, mode) => {
      el.addEventListener('click', () => {
        const idx     = sim.world.selectedCarIdx;
        const current = sim.getDriveMode(idx);
        const next    = current === mode ? 'normal' : mode;
        sim.setDriveMode(idx, next);
        refreshTeamPanel();
      });
    };
    wireDriveMode(tcPushEl,     'push');
    wireDriveMode(tcConserveEl, 'conserve');

    // Go-to-garage button — the only entry point into the race-results
    // modal now that auto-mount on phase=finished is gone. Pauses the
    // sim and lets the modal's near-opaque backdrop curtain off the
    // canvas behind. triggerRestart is a hoisted const declared above
    // (in the Esc-menu wiring) so it's already in scope here; the
    // closure captures it by reference.
    teamGarageEl?.addEventListener('click', () => {
      sim.setPaused(true);
      refreshPauseBtn();
      showRaceResults(mountEl, sim, triggerRestart);
    });
    const refreshTeamPanel = () => {
      const car = sim.world.cars[sim.world.selectedCarIdx];
      if (!car) {
        teamMetaEl.textContent = '—';
        return;
      }
      const num   = typeof car.carNumber === 'number' ? `#${car.carNumber} ` : '';
      const label = car.displayName || car.id.toUpperCase();
      teamMetaEl.textContent = `${num}${label}`;

      // Solo-mode rival selection swaps the panel into a "scouting"
      // view: no buttons (the player can't command a rival's team) but
      // a few pieces of info you'd plausibly know from watching the
      // race — visible damage, current stint length, total stops, and
      // the rival driver's profile. Banner / button states still take
      // priority for retired or finished rivals so the panel reads as
      // "they're done" rather than mid-race scouting.
      const rs           = sim.world.raceState;
      const idx          = sim.world.selectedCarIdx;
      const retired      = rs?.retiredCars instanceof Map && rs.retiredCars.has(idx);
      const finished     = !!car._raceFinished;
      const sessionOver  = rs?.phase === 'finished';
      const isSolo       = rs?.raceMode === 'solo';
      const isPlayerCar  = !isSolo || idx === rs?.soloCarIdx;
      const isRivalScout = isSolo && !isPlayerCar && !retired && !finished && !sessionOver;

      // Tag/LIVE indicator track the panel mode:
      // • "Team control" + LIVE — own car, race in progress
      // • "Scouting" — rival car, race in progress (solo mode)
      // • "Session complete" — race over, regardless of selection
      teamPanelEl.classList.toggle('is-scouting', isRivalScout);
      teamPanelEl.classList.toggle('is-finished', sessionOver);
      if (teamTagEl) {
        teamTagEl.textContent = sessionOver
          ? '// Session complete'
          : (isRivalScout ? '// Scouting' : '// Team control');
      }
      if (teamLiveEl) teamLiveEl.hidden = isRivalScout || sessionOver;

      // Race-over takes priority over the retire / finish banners. Once
      // the field is fully classified, the only meaningful action is
      // reviewing results — surface a single GO TO GARAGE button and
      // hide everything else. The HUD tick no longer auto-mounts the
      // results modal so the player can watch parc fermé / cool-down
      // laps as long as they want before clicking through.
      if (sessionOver) {
        teamStatusEl.hidden = true;
        teamBodyEl.hidden = true;
        if (teamScoutEl)    teamScoutEl.hidden = true;
        if (teamFinishedEl) teamFinishedEl.hidden = false;
        pitOrderEl.classList.add('hidden');
        return;
      }
      if (teamFinishedEl) teamFinishedEl.hidden = true;

      let banner = null;
      if (retired) {
        const cond = car.condition;
        const reason = cond?.damage > 0.5 ? 'CRASH'
                     : cond?.blownTires    ? 'BLOWN TIRES'
                     : cond?.fuel <= 0     ? 'OUT OF FUEL'
                     : 'RETIRED';
        banner = { text: `OUT OF RACE — ${reason}`, kind: 'retired' };
      } else if (finished) {
        banner = { text: 'Race completed - waiting for opponents to finish race', kind: 'finished' };
      }
      if (banner) {
        teamStatusEl.textContent = banner.text;
        teamStatusEl.dataset.kind = banner.kind;
        teamStatusEl.hidden = false;
        teamBodyEl.hidden = true;
        if (teamScoutEl) teamScoutEl.hidden = true;
        // The pit-order sub-panel reads off the same state — hide it
        // too so a retired car doesn't show a fuel slider.
        pitOrderEl.classList.add('hidden');
        return;
      }
      teamStatusEl.hidden = true;

      if (isRivalScout) {
        // Hide the action surface, show the scout block. Values come
        // from data the player could plausibly observe: damage (visible
        // on the car), pit-stop count, lap of the last stop (= stint
        // length), and the driver's authored profile bars.
        teamBodyEl.hidden = true;
        pitOrderEl.classList.add('hidden');
        if (teamScoutEl) teamScoutEl.hidden = false;
        const damagePct = damageDisplayPct(car.condition?.damage ?? 0);
        if (scoutDamageEl) scoutDamageEl.textContent = `${damagePct}%`;
        const curLap   = car.lapState?.lapNumber ?? 0;
        const lastPit  = car._lastPitLap;
        // Stint = laps since last pit. Before the first stop the stint
        // is "since lights out", so use lapNumber directly. lapNumber
        // can be 0 on the formation lap, which we render as "—" rather
        // than "0 lap" to avoid the impression of a fresh stop.
        let stintText = '—';
        if (lastPit != null && curLap >= lastPit) {
          const stint = curLap - lastPit;
          stintText = `${stint} lap${stint === 1 ? '' : 's'}`;
        } else if (curLap > 0) {
          stintText = `${curLap} lap${curLap === 1 ? '' : 's'}`;
        }
        if (scoutStintEl) scoutStintEl.textContent = stintText;
        if (scoutStopsEl) scoutStopsEl.textContent = String(car.pitStops ?? 0);
        const dp = car.driverProfile ?? {};
        const aggression = Math.max(0, Math.min(1, dp.aggression ?? 0.5));
        const skill      = Math.max(0, Math.min(1, dp.skill      ?? 0.5));
        if (scoutAggressionEl) scoutAggressionEl.style.width = `${(aggression * 100).toFixed(0)}%`;
        if (scoutSkillEl)      scoutSkillEl.style.width      = `${(skill      * 100).toFixed(0)}%`;
        return;
      }

      teamBodyEl.hidden = false;
      if (teamScoutEl) teamScoutEl.hidden = true;

      const ps = car.pitState;
      // The "in pit lane proper" gate: phase=pitting with the car at
      // or past the speed-zone start. The entry ribbon (pitting, but
      // before the zone) still allows the strategist to tweak the pit
      // order before the car reaches the box. The PIT button itself
      // locks once committed (cancellation mid-pit isn't supported),
      // but the order toggles stay live until the speed-zone board.
      // No speed zone authored → fall back to phase=pitting as the gate.
      const speedZone   = sim.world.geometry.pitSpeedZone;
      const carInPitZone = ps.phase === 'pitting' && (
        speedZone == null || car.derived.lineArc >= speedZone.startArc
      );
      // Button states map to workflow phases:
      //   default     → "PIT"          clickable, requests a stop
      //   requested   → "CANCEL PIT"   clickable, cancels the request
      //   pitting     → "IN PIT"       disabled, on the pit route
      //   boxStopped  → "SERVICING"    disabled, in the box (timer)
      const onPitRoute = ps.phase === 'pitting' || ps.phase === 'boxStopped';
      tcPitEl.classList.toggle('requested', !!ps.requested);
      tcPitEl.classList.toggle('active',    onPitRoute);
      if (!hasPitRoute) {
        tcPitEl.textContent = 'NO PIT';
        tcPitEl.disabled = true;
      } else if (ps.phase === 'boxStopped') {
        // Live countdown — serviceEndsAt is in sim seconds, world.simTime
        // ticks alongside. Floor to one decimal so the readout doesn't
        // flicker.
        const remaining = Math.max(0, (ps.serviceEndsAt ?? 0) - sim.world.simTime);
        tcPitEl.textContent = remaining > 0 ? `SERVICING — ${remaining.toFixed(1)}s` : 'SERVICING';
        tcPitEl.disabled = true;
      } else if (ps.phase === 'pitting') {
        tcPitEl.textContent = 'IN PIT';
        tcPitEl.disabled = true;
      } else if (ps.requested) {
        tcPitEl.textContent = 'CANCEL PIT';
        tcPitEl.disabled = false;
      } else {
        tcPitEl.textContent = 'PIT';
        tcPitEl.disabled = false;
      }

      // Reflect the selected car's order onto the toggle buttons,
      // and show the live ETA from the sim. ETA reflects current
      // condition (fuel level, damage), so it updates frame-by-frame.
      // The whole row hides once the player has committed to a stop —
      // mid-pit changes are confusing and the locked-out checkboxes
      // were visual noise.
      const order = sim.getPitOrder(sim.world.selectedCarIdx);
      if (order) {
        pitOptTiresEl.classList.toggle('active',  !!order.tires);
        pitOptFuelEl.classList.toggle('active',   !!order.fuel);
        pitOptRepairEl.classList.toggle('active', !!order.repair);
      }
      // Order buttons stay visible from the moment the player clicks
      // PIT all the way through the entry ribbon — only locking when
      // the car crosses into the speed-limit zone. Strategically
      // useful: the player can flip Tires / Fuel / Repair on/off
      // during the peel-off as they read the live ETA. Hidden during
      // normal racing (clean panel) and once the car is in pit proper
      // (order is locked at the box).
      const orderVisible = hasPitRoute
        && ((ps.phase === 'racing' && ps.requested) || (ps.phase === 'pitting' && !carInPitZone));
      pitOrderEl.classList.toggle('hidden', !orderVisible);
      const etaSec = sim.getPitServiceSeconds(sim.world.selectedCarIdx);
      pitEtaEl.textContent = `ETA ${etaSec.toFixed(1)}s`;

      // Slider state — bounds + values reflect current condition.
      // Fuel slider: range 0..(cap - currentFuel). When fuelKg is null
      // (default), pin the slider to max (= full fill). Skip writes
      // when the slider is focused (user is dragging) so we don't
      // fight their input mid-drag.
      const cap     = car.spec?.fuelCapacity ?? 0;
      const curFuel = car.condition?.fuel ?? 0;
      const room    = Math.max(0, cap - curFuel);
      const damage  = car.condition?.damage ?? 0;
      const fuelOn   = !!order?.fuel;
      const repairOn = !!order?.repair;
      pitFuelRowEl.classList.toggle('disabled',   !fuelOn);
      pitRepairRowEl.classList.toggle('disabled', !repairOn);
      pitFuelSliderEl.disabled   = !fuelOn   || !hasPitRoute;
      pitRepairSliderEl.disabled = !repairOn || !hasPitRoute;
      const focused = document.activeElement;
      // Fuel: clamp explicit kg to current room, fall back to room when null.
      const fuelKgEff = order?.fuelKg != null
        ? Math.max(0, Math.min(room, order.fuelKg))
        : room;
      if (focused !== pitFuelSliderEl) {
        pitFuelSliderEl.max   = String(Math.max(1, Math.round(room)));
        pitFuelSliderEl.value = String(Math.round(fuelKgEff));
      }
      pitFuelValEl.textContent = `${fuelKgEff.toFixed(0)} kg`;
      // Repair: stored as fraction 0..1, displayed as percent.
      const repairFracEff = order?.repairFrac != null
        ? Math.max(0, Math.min(1, order.repairFrac))
        : 1;
      if (focused !== pitRepairSliderEl) {
        pitRepairSliderEl.value = String(Math.round(repairFracEff * 100));
      }
      const damagePctTotal   = damageDisplayPct(damage);
      const damagePctRepair  = damageDisplayPct(damage * repairFracEff);
      pitRepairValEl.textContent = damagePctTotal > 0
        ? `${damagePctRepair}% / ${damagePctTotal}%`
        : `${Math.round(repairFracEff * 100)}%`;

      // Drive-mode highlight. .active reuses the same accent fill as
      // the pit "active" state — same visual language for "this team-
      // control command is currently in effect".
      const mode = sim.getDriveMode(sim.world.selectedCarIdx);
      tcPushEl.classList.toggle(    'active', mode === 'push');
      tcConserveEl.classList.toggle('active', mode === 'conserve');
    };
    refreshTeamPanel();

    // ---- spec panel ----
    // Hoisted so external call sites below (`renderPanel?.()`) remain
    // safe when the panel is gated off. Both bindings are only
    // populated inside the SHOW_SPEC_PANEL block; outside the block
    // renderPanel stays a no-op stub and panelCarIdx never advances.
    let unitSystem = readStoredUnit();
    let panelCarIdx = -1;
    let renderPanel = () => {};

    if (SHOW_SPEC_PANEL) {
    const panelEl     = mountEl.querySelector('#race-spec-panel');
    const headingEl   = mountEl.querySelector('#race-spec-heading');
    const bodyEl      = mountEl.querySelector('#race-spec-body');
    const titleEl     = mountEl.querySelector('#race-spec-title');
    const chevronEl   = mountEl.querySelector('#race-spec-chevron');

    titleEl.addEventListener('click', () => {
      panelEl.classList.toggle('collapsed');
      chevronEl.textContent = panelEl.classList.contains('collapsed') ? '▸' : '▾';
      // Render lazily on first expand so a closed panel costs nothing.
      if (!panelEl.classList.contains('collapsed')) renderPanel(true);
    });

    renderPanel = function(force) {
      const car = sim.world.cars[sim.world.selectedCarIdx] || sim.world.cars[0];
      if (!car) return;
      // Skip the rebuild if same car AND panel is hidden — no point
      // touching DOM the user can't see.
      if (panelEl.classList.contains('collapsed') && !force) return;
      if (sim.world.selectedCarIdx === panelCarIdx && !force) return;
      panelCarIdx = sim.world.selectedCarIdx;

      headingEl.textContent = `${car.id.toUpperCase()} spec`;

      const unitButtons = `
        <div class="race-spec-units">
          <button data-unit="metric"   class="${unitSystem === 'metric'   ? 'active' : ''}">Metric</button>
          <button data-unit="imperial" class="${unitSystem === 'imperial' ? 'active' : ''}">Imperial</button>
        </div>
      `;

      const sliderHtml = (p, value, prefix) => {
        const cfg = unitCfg(p, unitSystem);
        const sv  = cfg.fromSI(value);
        return `
          <div class="race-spec-slider">
            <div class="race-spec-row">
              <span class="label">${p.label}</span>
              <span class="value" id="rv-${prefix}-${p.key}">${cfg.fmt(sv)} ${cfg.unit}</span>
            </div>
            <input type="range" id="rs-${prefix}-${p.key}" min="${cfg.min}" max="${cfg.max}" step="${cfg.step}" value="${sv}" />
          </div>
        `;
      };

      const carSpecHtml    = TUNABLE.map(p => sliderHtml(p, car.spec[p.key], 'spec')).join('');
      const driverHtml     = DRIVER_TUNABLE.map(p => sliderHtml(p, car.driverProfile[p.key], 'drv')).join('');

      bodyEl.innerHTML = `
        ${unitButtons}
        <div class="race-spec-section">Car</div>
        ${carSpecHtml}
        <div class="race-spec-section">Driver</div>
        ${driverHtml}
      `;

      bodyEl.querySelectorAll('.race-spec-units button').forEach(btn => {
        btn.addEventListener('click', () => {
          unitSystem = btn.dataset.unit;
          writeStoredUnit(unitSystem);
          renderPanel(true);    // rebuild ranges in new units
        });
      });

      const wireSlider = (p, prefix, applyToTarget) => {
        const cfg = unitCfg(p, unitSystem);
        const slider  = bodyEl.querySelector(`#rs-${prefix}-${p.key}`);
        const readout = bodyEl.querySelector(`#rv-${prefix}-${p.key}`);
        slider.addEventListener('input', () => {
          const v = parseFloat(slider.value);
          // Re-fetch selected car each event — selection can change
          // mid-drag (via T&S panel click).
          const target = sim.world.cars[sim.world.selectedCarIdx] || sim.world.cars[0];
          if (target) applyToTarget(target, p.key, cfg.toSI(v));
          readout.textContent = `${cfg.fmt(v)} ${cfg.unit}`;
        });
      };

      TUNABLE.forEach(p =>
        wireSlider(p, 'spec', (target, key, val) => { target.spec[key] = val; })
      );
      DRIVER_TUNABLE.forEach(p =>
        wireSlider(p, 'drv', (target, key, val) => { target.driverProfile[key] = val; })
      );
    };
    } // end if (SHOW_SPEC_PANEL)

    // ---- timing & scoring panel ----
    // Rows are built ONCE at mount, one per car, and updated in place
    // on each tick. Sorting is visual only — flex `order` on each row
    // moves it without touching the DOM. This keeps the click handler
    // attached to stable nodes; previously the panel rebuilt innerHTML
    // every 250 ms, and a click whose mousedown landed on a row right
    // before a rebuild would silently fail (mouseup hit a different
    // element, no `click` event fired). Now rows are immortal until
    // unmount, so click reliability is guaranteed.
    const tsListEl = mountEl.querySelector('#race-ts-list');
    const tsMetaEl = mountEl.querySelector('#race-ts-meta');
    const tsRows   = new Map();   // car idx → row element

    // T&S status helpers. "Out" covers any condition that takes the
    // car out of contention until a yellow flag clears the field —
    // totaled (damage past the health cliff), out of fuel, or a
    // structural tire failure. The gap column shows OUT for these.
    // "Pitting" covers the entire pit route (entry ribbon, in-box,
    // exit ribbon); the gap reads PIT until they rejoin the racing
    // line. Both checks are pure reads — safe to call frame-by-frame.
    const carIsOut = (car) => {
      const c = car?.condition;
      if (!c) return false;
      return c.damage > 0.5 || c.fuel <= 0 || c.blownTires === true;
    };
    const carIsPitting = (car) => {
      const phase = car?.pitState?.phase;
      return phase != null && phase !== 'racing';
    };

    // Time-based gap to the car ahead, in seconds. The point of using
    // gate crossing times rather than instantaneous arc difference is
    // to kill rubberbanding: arc-based gaps fluctuate when the two
    // cars are at different track positions (one at apex, the other
    // mid-straight) because their speeds diverge. Anchoring both
    // measurements at the same physical line gives a value that
    // updates discretely at each crossing and stays stable in between.
    //
    // Anchored on the three timing gates (S/F + sector0 + sector1) so
    // the gap refreshes ~3× per lap on a 3-sector layout, which feels
    // broadcast-paced. Algorithm: for each gate both cars have crossed,
    // pick the gate where MY most-recent crossing is freshest overall
    // and ahead's crossing of the same gate gives a positive diff
    // (negative = ahead has lapped me at that gate). Returns null when
    // no valid pair exists; caller falls back to arc-based.
    const computeStableGapSec = (myCar, aheadCar) => {
      const mg = myCar?.lapState?.lastGateTimes;
      const ag = aheadCar?.lapState?.lastGateTimes;
      if (!mg || !ag) return null;
      let bestDiff = null;
      let bestMyTime = -Infinity;
      for (const g of ['sf', 's0', 's1']) {
        const m = mg[g];
        const a = ag[g];
        if (m == null || a == null) continue;
        const diff = m - a;
        if (diff < 0) continue;       // ahead is on the next lap of this gate
        if (m > bestMyTime) {
          bestMyTime = m;
          bestDiff   = diff;
        }
      }
      return bestDiff != null ? bestDiff / 1000 : null;
    };

    // Racing-line arc value at the S/F gate. Computed once at mount —
    // the racing line's arc=0 point is at its first dense sample
    // (wherever the user's authored waypoints happen to start), which
    // is generally NOT at S/F. Without this offset, sorting briefly
    // breaks at every S/F crossing: lapNumber increments instantly
    // but the racing-line arc doesn't wrap until the car physically
    // reaches the racing-line's own seam, which can be most of a lap
    // later. Cars appear to swap positions for a few seconds.
    let arcSF = 0;
    {
      const sfLine = sim.world.geometry.timingLines?.startFinish;
      const rl = sim.world.geometry.racingLine;
      if (sfLine && rl?.dense && rl.dense.length >= 4) {
        const np = nearestPointOnDense(sfLine.center, rl.dense, true);
        if (np) arcSF = arcLengthAtNp(np, rl);
      }
    }

    function computeStandings() {
      const line = sim.world.geometry.racingLine;
      const totalLen = line?.totalLength ?? 0;
      return sim.world.cars.map((car, idx) => {
        // Read raw arc from the racing-line cache, NOT the active-
        // line cache: the active cache switches to pit-route arc
        // space (much shorter) while pitting, which would crater a
        // pitting car's standings to last place. The racing-line
        // cache is always projected onto the racing line regardless
        // of phase. Cold cache → arc 0 (cars-on-grid before warm-up).
        const arcRaw = car.derived.racingLineSegIdx >= 0
          ? car.derived.racingLineArc
          : 0;
        // Normalize arc so 0 is at S/F (just past) and totalLen is
        // just-before-S/F. Synchronizes the arc wraparound with the
        // S/F-gate-driven lapNumber increment.
        const arc = totalLen > 0
          ? ((arcRaw - arcSF + totalLen) % totalLen)
          : 0;
        return { idx, car, lapNumber: car.lapState.lapNumber, arc };
      }).sort((a, b) => {
        if (a.lapNumber !== b.lapNumber) return b.lapNumber - a.lapNumber;
        return b.arc - a.arc;
      });
    }

    // ---- Race-action notifications ----
    // Detect events frame-by-frame and pop up small toasts that fade
    // out after a wall-clock TTL. Wall clock (not sim time) so the
    // user always has the same reading window regardless of sim speed
    // or pause. Detection itself runs each frame, so paused sims emit
    // nothing new — existing toasts hang until they expire on resume.
    const notifsEl       = mountEl.querySelector('#race-notifs');
    const notifQueue     = [];   // { id, text, kind, expiresAtMs }
    let   nextNotifId    = 0;
    const lastPosByCar   = new Map();   // carIdx → standings position
    const lastPhaseByCar = new Map();   // carIdx → pitState.phase
    const lastPassByPair = new Map();   // "passer:passed" → wallMs of last fire
    let   lastCautionActive    = false; // raceState.caution.active last frame
    let   lastCautionIncidents = 0;     // raceState.caution.incidents.length last frame
    let   lastCautionWarned    = false; // raceState.caution.warned last frame
    let   lastCleanupDone      = false; // simTime past caution.endsAt last frame
    let   lastPhase            = null;  // raceState.phase last frame
    let   whiteLapAnnounced    = false; // 'last lap' notif fires once per race
    const NOTIF_TTL_MS      = 6000;
    const PASS_COOLDOWN_MS  = 8000;     // don't refire same A-passes-B within this window
    const NOTIF_MAX_VISIBLE = 5;

    const carLabel = (car) => {
      const num = (typeof car?.carNumber === 'number') ? `#${car.carNumber} ` : '';
      const name = car?.displayName || car?.id?.toUpperCase() || 'CAR';
      return num + name;
    };
    const locationOf = (car) => {
      const corner = car?.derived?.currentCorner;
      if (corner) return `in ${corner}`;
      const area = car?.derived?.currentArea;
      if (area) return `at ${area}`;
      return '';
    };
    const pitReason = (order) => {
      if (!order) return '';
      const parts = [];
      if (order.tires)  parts.push('tires');
      if (order.fuel)   parts.push('fuel');
      if (order.repair) parts.push('repairs');
      if (parts.length === 0) return '';
      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
      return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
    };
    const pushNotif = (text, kind) => {
      notifQueue.push({
        id: ++nextNotifId,
        text,
        kind,
        expiresAtMs: Date.now() + NOTIF_TTL_MS,
      });
    };

    function detectEvents(standings) {
      const rs = sim.world.raceState;

      // Phase-transition announcements — these need to fire across the
      // pre-race / racing / cooldown boundaries, so they sit above the
      // "skip detection during pre-race" early-return below. Race start
      // is `pre-race → racing`; the checkered is `racing → cooldown`.
      const newPhase = rs?.phase ?? null;
      if (lastPhase !== newPhase) {
        if (lastPhase === 'pre-race' && newPhase === 'racing') {
          pushNotif('Green flag!', 'green');
        } else if (lastPhase === 'racing' && newPhase === 'cooldown') {
          // finishOrder[0] is whoever crossed S/F to trigger the
          // RACING → COOLDOWN flip — that's the winner. Fall back to
          // a no-name notif if the order hasn't been stamped yet (an
          // all-DNF abandon goes to FINISHED via the red-flag branch
          // and skips this transition entirely, so this is just
          // belt-and-braces).
          const winnerIdx = rs?.finishOrder?.[0];
          const winner = winnerIdx != null ? sim.world.cars[winnerIdx] : null;
          pushNotif(
            winner ? `Checkered flag! Congrats ${carLabel(winner)}` : 'Checkered flag!',
            'checkered',
          );
        }
        lastPhase = newPhase;
      }

      // Don't announce anything outside green-flag racing. Pre-race
      // grid shuffles and resets aren't "on track action"; cool-down
      // overtakes / pit entries / caution-cleanup events after the
      // checkered would just clutter the toast stack with non-news
      // (the race is already decided). The phase-transition notifs
      // above this gate (Green flag / Checkered flag) still fire.
      if (rs?.phase !== 'racing') {
        // Still seed lastPos / lastPhase so the first racing-frame
        // doesn't fire spurious events from cold state.
        lastPosByCar.clear();
        lastPhaseByCar.clear();
        for (let p = 0; p < standings.length; p++) {
          lastPosByCar.set(standings[p].idx, p + 1);
        }
        for (let i = 0; i < sim.world.cars.length; i++) {
          lastPhaseByCar.set(i, sim.world.cars[i].pitState?.phase ?? 'racing');
        }
        return;
      }
      const now    = Date.now();
      const cars   = sim.world.cars;
      const retired = rs?.retiredCars instanceof Map ? rs.retiredCars : null;
      const posNow = new Map();
      for (let p = 0; p < standings.length; p++) {
        posNow.set(standings[p].idx, p + 1);
      }

      // OVERTAKES — adjacent swaps in standings between two same-lap
      // racing cars. Filter out: pit-related shuffles (either car not
      // racing), retirees (sitting still cars rolled past don't count),
      // different lap counts (lapping traffic — dilutes announcer for
      // the MVP; can revisit later as a separate "lapped" event).
      for (const [idx, newPos] of posNow) {
        const oldPos = lastPosByCar.get(idx);
        if (oldPos == null) continue;
        if (newPos !== oldPos - 1) continue;             // gained exactly one
        // Find the car that was at newPos and dropped to oldPos.
        let passedIdx = null;
        for (const [otherIdx, otherOld] of lastPosByCar) {
          if (otherIdx === idx) continue;
          if (otherOld === newPos && posNow.get(otherIdx) === oldPos) {
            passedIdx = otherIdx;
            break;
          }
        }
        if (passedIdx == null) continue;
        const passer = cars[idx], passed = cars[passedIdx];
        if (!passer || !passed) continue;
        if (passer.pitState?.phase !== 'racing') continue;
        if (passed.pitState?.phase !== 'racing') continue;
        if (retired?.has(idx) || retired?.has(passedIdx)) continue;
        if (passer.lapState?.lapNumber !== passed.lapState?.lapNumber) continue;
        // Suppress opening-lap chaos. lapNumber=0 = pre-S/F (standing
        // start through to first crossing); lapNumber=1+ means the
        // car has settled into racing pace. Both cars need to clear
        // S/F at least once before any swap between them counts as
        // an "overtake" worth announcing.
        if ((passer.lapState?.lapNumber ?? 0) < 1) continue;
        if ((passed.lapState?.lapNumber ?? 0) < 1) continue;
        const pairKey = `${idx}:${passedIdx}`;
        const lastFire = lastPassByPair.get(pairKey);
        if (lastFire != null && now - lastFire < PASS_COOLDOWN_MS) continue;
        const where = locationOf(passer);
        const tail  = where ? ` ${where}` : '';
        pushNotif(`${carLabel(passer)} passed ${carLabel(passed)}${tail}`, 'pass');
        lastPassByPair.set(pairKey, now);
      }

      // PIT TRANSITIONS — fire only on racing → pitting (entry to the
      // pit route). The phase machine runs racing → pitting →
      // boxStopped → pitting → racing, so a less-specific gate would
      // fire a second time on the boxStopped → pitting "driving out
      // of the box" transition. Read the order at commit time;
      // mid-entry-ribbon order tweaks don't refire. Skip retired cars
      // (a totaled car that "pits" for the race-end park is not news).
      for (let i = 0; i < cars.length; i++) {
        const car   = cars[i];
        const phase = car.pitState?.phase ?? 'racing';
        const last  = lastPhaseByCar.get(i);
        if (last === 'racing' && phase === 'pitting' && !retired?.has(i)) {
          const reason = pitReason(car.pitState?.order);
          const tail   = reason ? ` for ${reason}` : '';
          pushNotif(`${carLabel(car)} pits${tail}`, 'pit');
        }
        lastPhaseByCar.set(i, phase);
      }

      // FULL COURSE CAUTION — announce caution start, additional
      // incidents while caution is already active, and the all-clear
      // when the cleanup timer expires. The race-state machine owns
      // the flag flip; we just narrate it.
      const caution = rs?.caution;
      if (caution) {
        const incidents = caution.incidents ?? [];
        if (caution.active && !lastCautionActive) {
          // Caution just triggered — name the first incident car.
          const firstInc = incidents[0];
          const firstCar = firstInc != null ? cars[firstInc.carIdx] : null;
          const tail = firstCar ? ` — ${carLabel(firstCar)} retired` : '';
          pushNotif(`Full course caution${tail}`, 'caution');
          // Yellow-light gantry briefly above the canvas, mirroring the
          // F1-style race-start gantry but in caution colors. Catches
          // the eye on a transition the player might otherwise miss
          // when focused on the cars.
          showFlagGantry(mountEl, 'yellow');
        } else if (caution.active && incidents.length > lastCautionIncidents) {
          // New incidents folded into the existing caution window.
          for (let k = lastCautionIncidents; k < incidents.length; k++) {
            const inc = incidents[k];
            const car = inc != null ? cars[inc.carIdx] : null;
            if (car) pushNotif(`${carLabel(car)} also retired`, 'caution');
          }
        } else if (!caution.active && lastCautionActive) {
          // Caution → green. Same wording as the race-start green-flag
          // notif so the player learns one phrase for "we are racing".
          pushNotif('Green flag!', 'green');
          // Green gantry on the restart — same eye-catching cue the
          // yellow gantry gives on caution start, applied to the
          // transition back to green.
          showFlagGantry(mountEl, 'green');
        }
        // Phase 1 → Phase 2 transition: the cleanup timer has expired
        // and any wreckage has been removed. Edge-detected on the
        // simTime ≥ endsAt boolean so a new incident extending endsAt
        // (which flips this back to false) lets the next completion
        // re-announce. Skip when there are no incidents — an active
        // caution with empty incidents shouldn't happen, but the guard
        // is cheap.
        const cleanupDone = caution.active
          && incidents.length > 0
          && sim.world.simTime >= caution.endsAt;
        if (cleanupDone && !lastCleanupDone) {
          pushNotif('Cars cleared up', 'caution');
        }
        lastCleanupDone = cleanupDone;
        // Racing-resumes warning. The race-state stamps `warned=true`
        // when the cleanup timer has expired AND the on-track leader
        // has entered the warn zone (~150 m before the green-flag
        // zone). Emit on the false→true edge. The flag resets when a
        // new incident pushes the timer past the leader, so the
        // warning correctly re-fires after a re-extended window.
        if (caution.active && caution.warned && !lastCautionWarned) {
          pushNotif('Green next time by!', 'caution');
        }
        lastCautionActive    = caution.active;
        lastCautionIncidents = incidents.length;
        lastCautionWarned    = caution.warned ?? false;
      }

      // White flag — leader has just started the final lap. Fires once
      // per race; survives a caution on the last lap (lapNumber is the
      // signal, flag color is independent). lapNumber increments on
      // S/F crossing, so leaderLap === totalLaps means "leader is now
      // running their last tour" regardless of yellow/green state.
      if (!whiteLapAnnounced && rs?.totalLaps > 0 && rs.phase === 'racing') {
        let leaderLap = 0;
        for (const c of cars) {
          const lap = c.lapState?.lapNumber ?? 0;
          if (lap > leaderLap) leaderLap = lap;
        }
        if (leaderLap === rs.totalLaps) {
          pushNotif('White flag, last lap!', 'white');
          whiteLapAnnounced = true;
        }
      }

      // Snapshot positions for next-frame diff.
      lastPosByCar.clear();
      for (const [idx, p] of posNow) lastPosByCar.set(idx, p);
    }

    function updateNotifications() {
      if (!notifsEl) return;
      const now = Date.now();
      // Expire from the front.
      while (notifQueue.length > 0 && notifQueue[0].expiresAtMs <= now) {
        notifQueue.shift();
      }
      // Visible window — keep the newest N.
      const visible = notifQueue.slice(-NOTIF_MAX_VISIBLE);
      const visibleIds = new Set(visible.map(n => n.id));
      // Remove DOM nodes whose notif has dropped out of the visible
      // window. Mark with .hide so CSS plays the fade-out, then drop
      // after the transition.
      for (const child of Array.from(notifsEl.children)) {
        const id = Number(child.dataset.notifId);
        if (!visibleIds.has(id) && !child.classList.contains('hide')) {
          child.classList.add('hide');
          child.classList.remove('show');
          setTimeout(() => child.remove(), 300);
        }
      }
      // Append new entries — those in `visible` whose id isn't in DOM.
      const existingIds = new Set();
      for (const child of notifsEl.children) {
        if (!child.classList.contains('hide')) {
          existingIds.add(Number(child.dataset.notifId));
        }
      }
      for (const n of visible) {
        if (existingIds.has(n.id)) continue;
        const el = document.createElement('div');
        el.className = `race-notif race-notif-${n.kind}`;
        el.dataset.notifId = String(n.id);
        el.textContent = n.text;
        notifsEl.appendChild(el);
        // Two-frame defer so the browser registers the initial state
        // before the .show class flips — without this the transition
        // doesn't play and the toast just appears.
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('show')));
      }
    }

    function buildTimingPanel() {
      tsListEl.innerHTML = '';
      tsRows.clear();
      // Roster-driven races set carNumber on each car; default-grid
      // races leave it undefined. Branching the panel-level template
      // keeps default-grid races compact (no empty number column),
      // while roster races get a livery-colored #NN badge slot.
      const hasNumbers = sim.world.cars.some(c => typeof c.carNumber === 'number');
      tsListEl.classList.toggle('with-numbers', hasNumbers);
      for (let i = 0; i < sim.world.cars.length; i++) {
        const car = sim.world.cars[i];
        const driverLabel = car.displayName ? car.displayName : car.id.toUpperCase();
        const num = typeof car.carNumber === 'number' ? car.carNumber : null;
        const liveryBg = car.livery?.body || 'transparent';
        // Only emit the number cell when the panel template includes a
        // column for it. Mismatched cell counts would shift later
        // children into the wrong grid slot.
        const numHtml = hasNumbers
          ? `<span class="ts-num" style="background:${escapeAttr(liveryBg)}">${num != null ? '#' + num : ''}</span>`
          : '';
        const row = document.createElement('div');
        row.className = `race-ts-row ${car.controllerType}`;
        row.dataset.carIdx = String(i);
        row.innerHTML = `
          <div class="race-ts-row-main">
            <span class="ts-pos">—</span>
            ${numHtml}
            <span class="ts-id">${escapeHtml(driverLabel)}</span>
            <span class="ts-mode">${car.controllerType === 'manual' ? 'MAN' : ''}</span>
            <span class="ts-time">—:——.———</span>
          </div>
          <div class="race-ts-row-sectors">
            <span></span>
            ${hasNumbers ? '<span></span>' : ''}
            <span class="ts-s1">S1 —</span>
            <span class="ts-s2">S2 —</span>
            <span class="ts-s3">S3 —</span>
          </div>
        `;
        tsListEl.appendChild(row);
        tsRows.set(i, row);
      }
      // Cap visible rows at 15 — fits a full series grid (10-team
      // championship + 5 expansion teams) without the panel having
      // to scroll. Beyond 15 the list scrolls. Measuring a row's
      // offsetHeight after layout is exact, vs. a CSS pixel guess
      // that drifts as font metrics or padding change. Inline style
      // overrides the CSS fallback.
      const firstRow = tsListEl.firstElementChild;
      if (firstRow) {
        const rowH = firstRow.getBoundingClientRect().height;
        const padY = 8; // matches .race-ts-list padding (4px top + 4px bottom)
        tsListEl.style.maxHeight = `${Math.ceil(rowH * 15 + padY)}px`;
      }
    }

    function updateTimingPanel() {
      const standings = computeStandings();
      tsMetaEl.textContent = `${sim.world.cars.length} car${sim.world.cars.length === 1 ? '' : 's'}`;

      // Pace estimate for converting arc-length gap → time gap. Use
      // the leader's last lap time once they've completed one;
      // before then, fall back to a static 50 m/s so intervals are
      // still meaningful. Arc differences are in metres; gap-time =
      // arc / pace.
      const leader = standings[0];
      const totalLen = sim.world.geometry.racingLine?.totalLength ?? 0;
      const leaderLast = leader?.car.lapState.lastLapTime;
      const paceMs = (leaderLast && leaderLast > 0 && totalLen > 0)
        ? totalLen / (leaderLast / 1000)
        : 50;

      // Field-best sector times across all cars in this session —
      // the "purple" baseline. Compute fresh each frame; cheap with
      // a handful of cars and avoids stale state on reset.
      const fieldBest = [null, null, null];
      for (const car of sim.world.cars) {
        const best = car.lapState.bestSectorTimes;
        for (let i = 0; i < 3; i++) {
          if (best[i] != null && (fieldBest[i] == null || best[i] < fieldBest[i])) {
            fieldBest[i] = best[i];
          }
        }
      }

      for (let position = 0; position < standings.length; position++) {
        const s = standings[position];
        const row = tsRows.get(s.idx);
        if (!row) continue;
        row.style.order = String(position);
        const isManual = s.car.controllerType === 'manual';
        const rs = sim.world.raceState;
        const isPlayerCar = rs?.raceMode === 'solo' && s.idx === rs.soloCarIdx;
        const isRetired   = rs?.retiredCars instanceof Map && rs.retiredCars.has(s.idx);
        row.classList.toggle('manual',  isManual);
        row.classList.toggle('ai',     !isManual);
        row.classList.toggle('selected', s.idx === sim.world.selectedCarIdx);
        row.classList.toggle('leader',  position === 0);
        row.classList.toggle('player-car', isPlayerCar);
        row.classList.toggle('retired', isRetired);

        row.querySelector('.ts-pos').textContent  = String(position + 1);
        // AI is the default; only flag manual drive so the badge
        // doesn't clutter every row with redundant "AI" tags.
        row.querySelector('.ts-mode').textContent = isManual ? 'MAN' : '';

        // Time / gap column.
        //
        //   Leader: live running clock for the current lap (resets on
        //     S/F crossing, ticks up). OUT LAP until first crossing.
        //
        //   Followers, lapped by LEADER: "+nL". Real F1 / IndyCar
        //     convention — "lapped" status is always measured
        //     relative to the race leader, not the car immediately
        //     ahead in standings. (If P2 has lapped P3 they're on
        //     different laps, but if P1 is still on the same lap as
        //     P3, P3 isn't a lap down — they're just behind the
        //     pack.)
        //
        //   Followers, same lap as leader: cumulative-distance gap
        //     to the car immediately ahead in standings → "+x.xxx"
        //     interval.
        let timeText;
        let timeState = '';   // '' (gap, white) | 'out' | 'pit'
        // Pre-S/F blank-out: covers both the pre-race grid hold AND the
        // standing-start window before the first crossing. Everything
        // begins when a car records its first S/F crossing — gap math
        // is meaningless before then. The "OUT LAP" / "GRID" labels are
        // intentionally not rendered for now; reserve them for future
        // qualifying / explicit out-lap signaling.
        if (carIsOut(s.car)) {
          timeText = 'OUT';
          timeState = 'out';
        } else if (carIsPitting(s.car)) {
          timeText = 'PIT';
          timeState = 'pit';
        } else if (s.lapNumber === 0) {
          timeText = '';
        } else if (position === 0) {
          const ls = s.car.lapState;
          // Once the leader has taken the checkered, freeze the time
          // column on their final lap time. Without this, currentLapStart
          // resets on the checkered S/F crossing and the panel keeps
          // counting up through the cool-down lap.
          if (s.car._raceFinished) {
            timeText = ls.lastLapTime != null ? fmtLap(ls.lastLapTime) : '—:——.———';
          } else if (ls.currentLapStart != null) {
            timeText = fmtLap(sim.world.simTime * 1000 - ls.currentLapStart);
          } else {
            timeText = '—:——.———';
          }
        } else {
          const leader      = standings[0];
          const leaderTotal = leader.lapNumber * totalLen + leader.arc;
          const myTotal     = s.lapNumber    * totalLen + s.arc;
          const lapsDownToLeader = totalLen > 0
            ? Math.floor((leaderTotal - myTotal) / totalLen)
            : 0;
          if (lapsDownToLeader >= 1) {
            timeText = `+${lapsDownToLeader}L`;
          } else {
            const ahead = standings[position - 1];
            // Prefer the S/F-anchored stable gap (no rubberbanding); fall
            // back to arc/pace when the cars don't yet share a comparable
            // crossing reference (lap 0, or ahead is >1 lap further along
            // without lapping us — an edge that arc-based handles fine).
            const stable = computeStableGapSec(s.car, ahead.car);
            const intervalSec = (stable != null)
              ? stable
              : (ahead.lapNumber * totalLen + ahead.arc - myTotal) / paceMs;
            timeText = `+${intervalSec.toFixed(3)}`;
          }
        }
        const tsTimeEl = row.querySelector('.ts-time');
        tsTimeEl.textContent = timeText;
        tsTimeEl.classList.toggle('is-out', timeState === 'out');
        tsTimeEl.classList.toggle('is-pit', timeState === 'pit');

        // Sector times — partial fallback to the just-finished lap.
        // Until the car crosses sector0 (= records its first new
        // sector this lap), all three cells show last lap's S1/S2/S3.
        // The MOMENT the new lap records its first sector, the
        // fallback turns off entirely: only sectors actually recorded
        // this lap show, the others go blank. Effect: blank cells
        // ahead of the car's current sector zone tell you at a glance
        // where each car is on track. ("S1 33.2 / S2 — / S3 —" → in
        // S2 zone; "S1 33.2 / S2 27.4 / S3 —" → in S3 zone.)
        // Motorsport coloring: purple = field best, green = personal
        // best, muted = normal time, subtle = no time yet. Cars that
        // are out of the race blank all three cells.
        const isOut = carIsOut(s.car);
        const cur  = isOut ? [] : (s.car.lapState.currentLapSectorTimes || []);
        const last = isOut ? [] : (s.car.lapState.lastSectorTimes       || []);
        const hasCurrentSector = cur.some(v => v != null);
        const personalBest = s.car.lapState.bestSectorTimes || [];
        const sectorIds = ['ts-s1', 'ts-s2', 'ts-s3'];
        for (let i = 0; i < 3; i++) {
          const cell = row.querySelector('.' + sectorIds[i]);
          const t = hasCurrentSector ? (cur[i] ?? null) : (last[i] ?? null);
          cell.textContent = `S${i + 1} ${fmtSectorShort(t)}`;
          cell.classList.remove('sec-overall', 'sec-personal', 'sec-normal', 'sec-empty');
          cell.classList.add(classifySector(t, personalBest[i], fieldBest[i]));
        }
      }
      return standings;
    }

    function classifySector(time, personalBest, fieldBest) {
      if (time == null) return 'sec-empty';
      if (fieldBest != null && time <= fieldBest)       return 'sec-overall';
      if (personalBest != null && time <= personalBest) return 'sec-personal';
      return 'sec-normal';
    }

    // Compact sector format — "33.1" or "—" if no time yet. fmtSector
    // from lap-timing.js gives a more verbose form; we want one-decimal
    // seconds so three sectors fit on one line.
    function fmtSectorShort(ms) {
      if (ms == null) return '—';
      return (ms / 1000).toFixed(1);
    }

    tsListEl.addEventListener('click', e => {
      const row = e.target.closest('[data-car-idx]');
      if (!row) return;
      const idx = Number(row.dataset.carIdx);
      // Out-of-race cars (retired) aren't valid spectator targets —
      // their physics has stopped, the camera would just sit on a
      // wreck. Same gate the arrow-key navigator uses below.
      const rs = sim.world.raceState;
      if (rs?.retiredCars instanceof Map && rs.retiredCars.has(idx)) return;
      sim.selectCar(idx);
      updateTimingPanel();   // immediate visual feedback
      renderPanel(false);    // spec panel may need to re-render too
    });

    buildTimingPanel();
    updateTimingPanel();

    const tickHud = () => {
      if (cancelled) return;
      const car = sim.world.cars[sim.world.selectedCarIdx] || sim.world.cars[0];
      if (!car) {
        hudRaf = requestAnimationFrame(tickHud);
        return;
      }
      // Solo-mode telemetry gate. Player sees full telemetry only on
      // their locked driver; everyone else gets position + lap + best
      // and nothing else (the [data-solo-private] sections collapse via
      // CSS). Team-control panel switches into a "scouting" view for
      // rivals — that toggle lives in refreshTeamPanel since it owns
      // the panel's mode-specific rendering.
      const rs = sim.world.raceState;
      const isSolo = rs?.raceMode === 'solo';
      const isPlayerCar = !isSolo || sim.world.selectedCarIdx === rs?.soloCarIdx;
      // Debug mode bypasses the solo-private gate so the user can
      // inspect any car's full telemetry.
      const hidePrivate = isSolo && !isPlayerCar && !debugMode;
      if (tlmBodyEl) tlmBodyEl.classList.toggle('solo-private', hidePrivate);
      if (debugMode && sim.getPerfMetrics) {
        const m = sim.getPerfMetrics();
        if (m) {
          if (dbgFpsEl)    dbgFpsEl.textContent    = m.fps.toFixed(1);
          if (dbgFrameEl)  dbgFrameEl.textContent  = m.avgFrameMs.toFixed(2)  + ' ms';
          if (dbgTickEl)   dbgTickEl.textContent   = m.avgTickMs.toFixed(2)   + ' ms';
          if (dbgRenderEl) dbgRenderEl.textContent = m.avgRenderMs.toFixed(2) + ' ms';
        }
        // Selected-car readout. Manual cars don't run an AI controller
        // so driveMode is meaningless for them — show MANUAL instead so
        // the user knows they're in the wheel rather than directing AI.
        if (dbgCarEl) {
          const num = (typeof car.carNumber === 'number') ? `#${car.carNumber} ` : '';
          const name = car.displayName || car.id?.toUpperCase() || `CAR${sim.world.selectedCarIdx}`;
          dbgCarEl.textContent = `${num}${name}`;
        }
        if (dbgDriveEl) {
          const mode = car.controllerType === 'manual'
            ? 'MANUAL'
            : (car.driveMode || 'normal').toUpperCase();
          dbgDriveEl.textContent = mode;
          dbgDriveEl.dataset.mode = mode.toLowerCase();
        }
        // Skip slider writes while the user is dragging — otherwise the
        // per-frame value snap-back fights the drag and the slider
        // visually jitters to whatever the physics tick decided since
        // input started.
        const cond = car.condition;
        const cap  = car.spec?.fuelCapacity ?? 1;
        if (cond) {
          if (dbgTiresEl && document.activeElement !== dbgTiresEl) {
            const t = Math.round(cond.tireLife * 100);
            dbgTiresEl.value = String(t);
            if (dbgTiresValEl) dbgTiresValEl.textContent = `${t}%`;
          }
          if (dbgFuelEl && document.activeElement !== dbgFuelEl) {
            const f = Math.round((cond.fuel / cap) * 100);
            dbgFuelEl.value = String(f);
            if (dbgFuelValEl) dbgFuelValEl.textContent = `${f}%`;
          }
          if (dbgDmgEl && document.activeElement !== dbgDmgEl) {
            const d = Math.round(cond.damage * 100);
            dbgDmgEl.value = String(d);
            if (dbgDmgValEl) dbgDmgValEl.textContent = `${d}%`;
          }
        }
      }
      updateRaceControl(rs, sim.world);
      const ls = car.lapState;
      const liveLap = ls.currentLapStart != null ? sim.world.simTime * 1000 - ls.currentLapStart : null;
      const driveTag = car.controllerType === 'manual' ? 'MANUAL' : 'AI';
      const num = typeof car.carNumber === 'number' ? car.carNumber : null;
      const abbr = car.displayName || null;
      // Header reads "#33 VER" for roster races and falls back to the
      // synthetic id ("CAR0 · AI") for default-grid runs. Manual mode
      // is appended in either case so the player still sees when they
      // have the wheel.
      const headerText = abbr
        ? `${num != null ? '#' + num + ' ' : ''}${abbr}${car.controllerType === 'manual' ? ' · MAN' : ''}`
        : `${car.id.toUpperCase()} · ${driveTag}`;

      // Field-best lap (across all cars) for purple-highlighting the
      // best column. Recomputed every frame; cheap with a few cars.
      let fieldBestLap = null;
      for (const other of sim.world.cars) {
        const b = other.lapState.bestLapTime;
        if (b != null && (fieldBestLap == null || b < fieldBestLap)) fieldBestLap = b;
      }
      // Field-best sectors for the telemetry sector cells.
      const fieldBestSec = [null, null, null];
      for (const other of sim.world.cars) {
        const b = other.lapState.bestSectorTimes;
        for (let i = 0; i < 3; i++) {
          if (b[i] != null && (fieldBestSec[i] == null || b[i] < fieldBestSec[i])) fieldBestSec[i] = b[i];
        }
      }

      // ---- header ----
      tlmMetaEl.textContent = headerText;
      // Driver / team detail — only rendered when the roster picker
      // populated the car. Default-grid races (no displayName/team)
      // hide the section so the panel doesn't grow an empty row.
      const showDriverSection = !!(car.fullName || car.team);
      tlmDrvSecEl.hidden = !showDriverSection;
      if (showDriverSection) {
        tlmDrvNameEl.textContent = car.fullName || car.displayName || '';
        tlmDrvTeamEl.textContent = car.team?.name || '';
      }

      // ---- speed / surface ----
      const speed = Math.hypot(car.vel.x, car.vel.y);
      tlmSpeedEl.textContent = fmtSpeed(speed, unitSystem);
      tlmSurfEl.textContent  = car.derived.surface.toUpperCase();

      // ---- timing ----
      // Lap row stays blank until the first S/F crossing — covers both
      // pre-race (sitting on grid) and the standing-start window. The
      // "OUT LAP" / "GRID" labels are intentionally not rendered for
      // now; reserved for future qualifying / explicit out-lap states.
      tlmLapEl.textContent  = ls.lapNumber === 0
        ? ''
        : `${ls.lapNumber} · ${fmtLap(liveLap)}`;
      tlmLastEl.textContent = fmtLap(ls.lastLapTime);
      tlmBestEl.textContent = fmtLap(ls.bestLapTime);
      // Best lap purple if it's the session-best, green if it's just
      // this car's PB. Apply to the value's classList directly.
      tlmBestEl.classList.remove('sec-overall', 'sec-personal');
      if (ls.bestLapTime != null && fieldBestLap != null && ls.bestLapTime <= fieldBestLap) {
        tlmBestEl.classList.add('sec-overall');
      } else if (ls.bestLapTime != null) {
        tlmBestEl.classList.add('sec-personal');
      }
      // Last lap: purple if it set a new field best, green if it was
      // a personal best.
      tlmLastEl.classList.remove('sec-overall', 'sec-personal');
      if (ls.lastLapTime != null) {
        if (fieldBestLap != null && ls.lastLapTime <= fieldBestLap) {
          tlmLastEl.classList.add('sec-overall');
        } else if (ls.bestLapTime != null && ls.lastLapTime <= ls.bestLapTime) {
          tlmLastEl.classList.add('sec-personal');
        }
      }

      // Sector cells — same F1 fallback as the T&S panel: prefer
      // current-lap sector time if recorded, else fall back to the
      // previous lap's. Keeps S3 visible across the S/F transition.
      const curSec  = ls.currentLapSectorTimes || [];
      const lastSec = ls.lastSectorTimes || [];
      const pbSec   = ls.bestSectorTimes || [];
      const secEls  = [tlmS1El, tlmS2El, tlmS3El];
      for (let i = 0; i < 3; i++) {
        const t = curSec[i] ?? lastSec[i] ?? null;
        secEls[i].textContent = `S${i + 1} ${t == null ? '—' : (t / 1000).toFixed(1)}`;
        secEls[i].className = classifySector(t, pbSec[i], fieldBestSec[i]);
      }

      // ---- inputs (throttle / brake / steer) ----
      const throttle = car.lastInputs?.throttle ?? 0;
      const brake    = car.lastInputs?.brake ?? 0;
      tlmThrValEl.textContent  = `${Math.round(throttle * 100)}%`;
      tlmThrFillEl.style.width = `${Math.max(0, Math.min(1, throttle)) * 100}%`;
      tlmBrkValEl.textContent  = `${Math.round(brake * 100)}%`;
      tlmBrkFillEl.style.width = `${Math.max(0, Math.min(1, brake)) * 100}%`;

      // Steer indicator. Map signed steer (−maxSteeringAngle..+max) to
      // a center-anchored bar fill.
      const maxSteer = car.spec.maxSteeringAngle || 1;
      const steerRatio = Math.max(-1, Math.min(1, car.steer / maxSteer));
      const steerDeg   = car.steer * 180 / Math.PI;
      tlmStrValEl.textContent = `${steerDeg >= 0 ? '+' : ''}${steerDeg.toFixed(0)}°`;
      if (steerRatio >= 0) {
        tlmStrFillEl.style.left  = '50%';
        tlmStrFillEl.style.width = `${(steerRatio * 50).toFixed(1)}%`;
      } else {
        tlmStrFillEl.style.left  = `${(50 + steerRatio * 50).toFixed(1)}%`;
        tlmStrFillEl.style.width = `${(-steerRatio * 50).toFixed(1)}%`;
      }

      // ---- G forces ----
      const latG  = car.derived.lateralG ?? 0;
      const longG = car.derived.longG ?? 0;
      const latArrow  = latG  >  0.05 ? ' ←' : latG  < -0.05 ? ' →' : '';
      const longArrow = longG >  0.05 ? ' ↑' : longG < -0.05 ? ' ↓' : '';
      tlmLatGEl.textContent  = `${Math.abs(latG).toFixed(2)}g${latArrow}`;
      tlmLongGEl.textContent = `${Math.abs(longG).toFixed(2)}g${longArrow}`;

      // ---- condition (tires / fuel / damage) ----
      const cond = car.condition;
      if (cond) {
        const tirePct = Math.round(cond.tireLife * 100);
        const compoundName = car.spec.tireCompound || 'medium';
        const compound     = getCompound(compoundName);
        const compoundLabel = compound.label;
        tlmTireLblEl.textContent = `Tires (${compoundLabel})`;
        if (cond.blownTires) {
          tlmTireValEl.textContent = 'BLOWN';
          tlmTireValEl.style.color = '#e54b3c';
        } else {
          tlmTireValEl.textContent = `${tirePct}%`;
          tlmTireValEl.style.color = '';
        }
        tlmTireFillEl.style.width = `${Math.max(0, Math.min(1, cond.tireLife)) * 100}%`;

        // Temp + zone label, color-coded by where the tire sits in
        // the compound's grip curve. The text is the source of truth
        // for "is the driver getting peak grip?" so it gets the most
        // visual weight. Below-window and at-floor both read "cold"
        // — the difference is visual (deeper blue when further off),
        // and the wear/grip math handles the rest.
        const t = cond.tireTemp;
        let zone = 'optimal', tempColor = '#6cdf6c';
        if (t < compound.tempColdLimit)            { zone = 'cold';        tempColor = '#5dade2'; }
        else if (t < compound.tempOptimalLow)      { zone = 'cold';        tempColor = '#86c5da'; }
        else if (t <= compound.tempOptimalHigh)    { zone = 'optimal';     tempColor = '#6cdf6c'; }
        else if (t < compound.tempHotLimit)        { zone = 'hot';         tempColor = '#e8842c'; }
        else                                        { zone = 'overheating'; tempColor = '#e54b3c'; }
        tlmTireTempEl.textContent = `${t.toFixed(0)}°C — ${zone}`;
        tlmTireTempEl.style.color = tempColor;

        const fuelCap = car.spec.fuelCapacity || 1;
        const fuelPct = Math.round((cond.fuel / fuelCap) * 100);

        // Fuel-laps estimate. Stable readout — only updates at lap
        // boundaries, NEVER per-tick. Per-tick projection swings wildly
        // (full-throttle straight vs brake zone) and isn't what the
        // player wants — they want "how much fuel is on board, in
        // laps", not "how much fuel until I let off this corner".
        //
        // Two sources, best-first:
        //   1. Measured burn from the last completed flying lap.
        //      Updates once per S/F crossing. Exact for a representative
        //      lap. Skipped for the standing-start lap (low-gear-off-
        //      the-grid burn is unrepresentative of flying pace).
        //   2. Heuristic burnRate × dutyCycle × lapTime. Only used
        //      before the first flying lap completes. lapTime falls
        //      back from lastLapTime → bestLapTime → trackLen/35 m/s
        //      → 90 s.
        const carIdx       = sim.world.selectedCarIdx;
        const lapNumber    = car.lapState?.lapNumber ?? 0;
        const racingLineLen= sim.world.geometry?.racingLine?.totalLength ?? 0;

        let s = fuelTrackers.get(carIdx);
        const lapChanged = s && s.lap !== lapNumber;
        const refueled   = s && cond.fuel > s.lapStartFuel + 1;
        const reset      = s && lapNumber < s.lap;
        if (!s || reset) {
          s = {
            lap: lapNumber,
            lapStartFuel: cond.fuel,
            lastLapFuelBurn: null,
            lapTransitions: 0,
          };
          fuelTrackers.set(carIdx, s);
        } else if (lapChanged) {
          const burned = s.lapStartFuel - cond.fuel;
          s.lapTransitions++;
          // Skip the first lap rollover — standing-start lap burn is
          // anomalously high (low gear off the grid + shorter than
          // a flying lap). Using it as the baseline made the lap-2
          // readout misleadingly low.
          if (burned > 0 && s.lapTransitions > 1) {
            s.lastLapFuelBurn = burned;
          }
          s.lap = lapNumber;
          s.lapStartFuel = cond.fuel;
        } else if (refueled) {
          s.lapStartFuel = cond.fuel;
        }

        let burnPerLap;
        if (s.lastLapFuelBurn != null) {
          burnPerLap = s.lastLapFuelBurn;
        } else {
          const burnRate = car.spec?.fuelBurnRate ?? 0.05;
          const dutyCycle = 0.6;
          const lapMs = car.lapState?.lastLapTime
            ?? car.lapState?.bestLapTime
            ?? (racingLineLen ? (racingLineLen / 35) * 1000 : 90000);
          burnPerLap = burnRate * dutyCycle * (lapMs / 1000);
        }
        // Suffix appended to the fuel readout — e.g. "~30.5L". Empty
        // when out of fuel (the OUT message stands on its own) or when
        // we can't form a meaningful estimate.
        const lapsSuffix = (cond.fuel > 0 && burnPerLap > 0)
          ? ` ~${(cond.fuel / burnPerLap).toFixed(1)}L`
          : '';

        if (cond.fuel <= 0) {
          tlmFuelValEl.textContent = 'OUT — coasting';
          tlmFuelValEl.style.color = '#e54b3c';
        } else if (fuelPct < 8) {
          tlmFuelValEl.textContent = `${cond.fuel.toFixed(1)} kg (${fuelPct}%)${lapsSuffix} — LOW`;
          tlmFuelValEl.style.color = '#f5c441';
        } else {
          tlmFuelValEl.textContent = `${cond.fuel.toFixed(1)} kg (${fuelPct}%)${lapsSuffix}`;
          tlmFuelValEl.style.color = '';
        }
        tlmFuelFillEl.style.width = `${Math.max(0, Math.min(1, cond.fuel / fuelCap)) * 100}%`;

        const dmgPct = damageDisplayPct(cond.damage);
        tlmDmgValEl.textContent  = `${dmgPct}%${cond.damage > DAMAGE_TOTALED_THRESHOLD ? ' — TOTALED' : ''}`;
        tlmDmgFillEl.style.width = `${dmgPct}%`;

        if (tlmPitStopsEl) tlmPitStopsEl.textContent = String(car.pitStops ?? 0);
      }

      // T&S panel update — in-place, no DOM rebuild. Cheap enough
      // to run every frame; rows are stable so clicks never miss.
      const standings = updateTimingPanel();
      // On-track action notifications. Detection reuses the standings
      // we just computed (cheap diff vs. last frame). Render runs every
      // frame so the fade timeline ticks even when no events fire.
      detectEvents(standings);
      updateNotifications();
      // Team-control panel mirrors the selected car's pit state, so
      // it needs to refresh both on selection changes and when the
      // phase machine flips (request → pitting → racing).
      refreshTeamPanel();
      // Re-render the spec panel if the selected car changed.
      if (sim.world.selectedCarIdx !== panelCarIdx) renderPanel(false);
      // Race-end overlay used to auto-mount on phase=finished, but the
      // player asked to keep watching the cars after the checkered. The
      // team-control panel surfaces a "Go to garage" button when the
      // session is complete — that button is the modal's only entry
      // point now.
      hudRaf = requestAnimationFrame(tickHud);
    };
    hudRaf = requestAnimationFrame(tickHud);
  } catch (err) {
    stopImageRamp();
    if (curtainStatusEl) curtainStatusEl.textContent = `Failed to load track: ${err.message}`;
    if (curtainEl) curtainEl.classList.add('error');
  }

  return teardown;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
